import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Timeline, type TimelineState } from '@xzdarcy/react-timeline-editor';
import '@xzdarcy/react-timeline-editor/dist/react-timeline-editor.css';
import {
  ASPECTS,
  DEFAULT_OVERLAY_MS,
  DEFAULT_PHOTO_MS,
  DEFAULT_STRAP_MS,
  DEFAULT_TRANSITION_MS,
  EFFECT_TYPES,
  FILTERS,
  KEY_PROPS_OF,
  MAX_GAIN,
  MOTIONS,
  TRANSITION_TYPES,
  audioAt,
  audioClipId,
  audioEndMs,
  audioLengthMs,
  audioSourceKey,
  captionsForClip,
  clampProject,
  defaultLook,
  detachSound,
  dipId,
  editDurationMs,
  frameSize,
  freezeAt,
  insertSegment,
  isPieceSound,
  isPlainCut,
  isStill,
  keyIndexAt,
  keyTimes,
  keyValue,
  makeOverlay,
  makeSegment,
  makeText,
  moveSegment,
  overlayId,
  pieceSounds,
  relinkSound,
  removeKey,
  removeSegment,
  segmentAt,
  segmentId,
  segmentLengthMs,
  segmentStarts,
  setKey,
  setTransitionNear,
  sourceKey,
  splitAllAt,
  splitAt,
  splitAudioAt,
  splitTextAt,
  strapId,
  STRAP_ANIM,
  STRAP_DESIGNS,
  strapAnim,
  strapDesign,
  strapDesignFor,
  textId,
  type AnimIn,
  type AnimOut,
  type AudioTrack,
  type EditAudioClip,
  type EditAudioSource,
  type EditDip,
  type EditEffect,
  type EditFormat,
  type EditKeys,
  type EditLook,
  type EditMotion,
  type EditOutput,
  type EditOverlay,
  type EditProject,
  type EditSegment,
  type EditSource,
  type EditStrap,
  type EditText,
  type EditTextStyle,
  type EditTransform,
  type EffectType,
  type FilterType,
  type KeyProp,
  type KeyedKind,
  type Lane,
  type MotionType,
  type SourceDurations,
  type StrapDesignKey,
  type StrapTemplate,
  type TransitionType,
} from '@airpost/shared';
import { ApiError, del, get, post, put } from '../api.js';
import { EditEngine } from '../edit-engine.js';
import { browserStore, draftToOffer, dropDraft, keepDraft } from '../edit-draft.js';
import { exportEdit, fastExportCodecs, type FastCodecs } from '../edit-export.js';
import { analyseSound, analyseSource, audioUrlOf, captureFrames, peakOf, sourceUrlOf, type SourceAnalysis } from '../edit-media.js';
import { lookPaint } from '../edit-look.js';
import { renderMime, type Box, type RenderResult } from '../edit-render.js';
import {
  ROW,
  ROW_HEIGHT,
  ROW_KEYS,
  RULER_PX,
  SCALES,
  SCALE_WIDTH,
  START_LEFT,
  audioIdOf,
  dipIdOf,
  fitScaleIndex,
  overlayIdOf,
  fromDb,
  pressTravelled,
  projectFromRows,
  pxPerSecond,
  rowOfSelection,
  rowsOf,
  scaleLabel,
  segmentIdOf,
  selectionOf,
  selectionOfRow,
  strapIdOf,
  textIdOf,
  timecode,
  toDb,
  type RowKey,
  type Selection,
  type TlAction,
  type TlRow,
} from '../edit-timeline.js';
import { t } from '../i18n.js';
import { letterKey } from '../keys.js';
import { Link, navigate, setLeaveGuard, useLocation } from '../router.js';
import { useSession } from '../session.js';
import { mmss } from '../util.js';
import { AnimFields, EFFECT_WORDS, EffectsPanel, FILTER_WORDS, FormatPanel, FramePanel, LookPanel, MOTION_WORDS, OverlayPanel, SpeedPanel, TEXT_PRESETS, TEXT_TEMPLATES, TemplateThumb, TextPanel, presetById, presetCss, type KeyKit } from './edit-panels.js';
import { VoiceoverPanel } from './voiceover-panel.js';
import { AirDialog, airLine, useAir } from './air-dialog.js';
import { StrapArtView, StrapDesignContext } from '../strap-view.js';

/**
 * Μοντάζ: a sequence on a timeline, the way an editing suite lays it out. On the left the bins: the playout's
 * library, the videos and photos the journalist imports, the sounds (takes and beds), texts, transitions, filters,
 * effects, the lower thirds and the format, each dragged onto the timeline or the monitor (or added with ＋). The
 * program monitor is a canvas the engine draws exactly as the export will (`edit-engine.ts`), with all the sound
 * mixed on Web Audio and metered; a text or a picture is dragged on it into place. The tracks (the timeline of
 * @xzdarcy/react-timeline-editor): V2 the pictures over the sequence, V1 the pieces with their transitions, T1 the
 * straps, T2 the texts and captions, A1 the voice-over clips, A2 the music clips and their dips; every item is cut
 * (C on its track, Shift+C on all), trimmed, moved and removed; a piece has its colour, frame, move, speed and
 * effects, F freezes its frame; each track has its level, mute and solo in the mixer. Every change can be undone.
 * "✦ Μοντάζ με AI" sends frames of the clips to the AI editor and applies its edit as one undoable change. The
 * project saves itself; "Αποθήκευση αλλαγών" writes the edit as an MP4 of the format in the browser and saves it with
 * the story; "Στο storyline" puts the edit into the story.
 */
interface Media {
  id: string;
  title: string;
  filename: string;
  durationMs: number;
  status: string;
}
interface LibraryClip {
  id: string;
  title: string;
  filename: string;
  durationMs: number;
  status: string;
  uses: number;
}
interface Upload {
  id: string;
  title: string;
  filename: string;
  mime: string;
  durationMs: number | null;
  bytes: number;
  createdAt: string;
}
interface EditDto {
  id: string;
  storyId: string;
  storyTitle: string;
  storyText: string;
  mediaAssetId: string;
  media: Media | null;
  title: string;
  project: EditProject;
  revision: number;
  rendered: { mime: string | null; durationMs: number | null; at: string; bytes: number } | null;
  createdBy: string | null;
  updatedAt: string;
  canEdit: boolean;
  /** The story's latest bulletin: its straps' design unless the edit chose one. */
  programmeName: string | null;
  uploads?: Upload[];
  library?: LibraryClip[];
}
interface MusicTrack {
  id: string;
  title: string;
  mime: string;
  durationMs: number | null;
}
interface Voiceover {
  id: string;
  kind: 'recorded' | 'ai';
  text: string;
  voice: string | null;
  durationMs: number | null;
  createdBy: string | null;
}
interface Wave {
  durationMs: number;
  wave: string;
}
/** What a card of a bin carries when dragged onto the timeline. */
type DragPayload =
  | { kind: 'clip' | 'upload'; id: string; durationMs: number }
  | { kind: 'strap'; template: StrapTemplate }
  | { kind: 'take' | 'bed'; id: string; durationMs: number | null }
  | { kind: 'transition'; type: TransitionType }
  | { kind: 'endfade'; color: 'black' | 'white' }
  | { kind: 'image'; id: string }
  | { kind: 'text'; preset: string }
  | { kind: 'filter'; filter: FilterType }
  | { kind: 'effect'; type: EffectType }
  | { kind: 'motion'; type: MotionType };
type BinTab = 'library' | 'uploads' | 'images' | 'audio' | 'text' | 'transitions' | 'filters' | 'effects' | 'lt' | 'format';
type PieceTab = 'basic' | 'color' | 'frame' | 'speed' | 'effects';
const BIN_TABS: Array<{ id: BinTab; icon: string; label: () => string }> = [
  { id: 'library', icon: '🎬', label: () => t('veBinLibrary') },
  { id: 'uploads', icon: '⤒', label: () => t('veBinUploads') },
  { id: 'images', icon: '🖼', label: () => t('veBinImages') },
  { id: 'audio', icon: '♪', label: () => t('veBinAudio') },
  { id: 'text', icon: 'Aa', label: () => t('veBinText') },
  { id: 'transitions', icon: '⧓', label: () => t('veBinTransitions') },
  { id: 'filters', icon: '◐', label: () => t('veBinFilters') },
  { id: 'effects', icon: '✨', label: () => t('veBinEffects') },
  { id: 'lt', icon: 'T', label: () => t('veBinLt') },
  { id: 'format', icon: '▭', label: () => t('veBinFormat') },
];
const PIECE_TABS: Array<{ id: PieceTab; label: () => string }> = [
  { id: 'basic', label: () => t('veTabBasic') },
  { id: 'color', label: () => t('veTabColor') },
  { id: 'frame', label: () => t('veTabFrame') },
  { id: 'speed', label: () => t('veTabSpeed') },
  { id: 'effects', label: () => t('veTabEffects') },
];

const DND = 'application/x-airpost-edit';
const FRAME_MS = 40;
const THUMB_H = 42;
const THUMB_W = Math.round((THUMB_H * 16) / 9);
const MAX_IMPORT_BYTES = 200 * 1024 * 1024;
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const isImageUpload = (u: { mime: string }) => u.mime.startsWith('image/');
/** A dip the journalist adds at the playhead: four seconds, the music down to a fifth. */
const DIP_MS = 4000;
const DIP_LEVEL = 0.2;
const HISTORY_MAX = 100;
/** Changes of the same kind closer than this make one step of undo (typing, a slider). */
const COALESCE_MS = 800;
/** The timeline's toolbar, over its tracks. */
const TL_BAR = 36;
const PIECE_ICONS: Record<PieceTab, string> = { basic: '☰', color: '◐', frame: '⤢', speed: '»', effects: '✦' };
const MON_ZOOMS: Array<'fit' | number> = ['fit', 0.5, 0.75, 1.5, 2];
/** The browser's memory of the editor's layout (the timeline's height, the monitor's guides). */
const pref = <T,>(key: string, fallback: T): T => {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
};
const savePref = (key: string, v: unknown) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* the layout is not remembered */
  }
};
/** An item whose place, size, turn and opacity have keyframes. */
type ItemRef = { kind: KeyedKind; id: string };
interface ItemInfo {
  start: number;
  length: number;
  keys: EditKeys;
  base: Partial<Record<KeyProp, number>>;
}
/** A style copied from an item, to paste on another of its kind. */
type StyleClip =
  | { kind: 'segment'; look: EditLook; transform: EditTransform; motion: EditMotion; effects: EditEffect[] }
  | { kind: 'text'; style: EditTextStyle; animIn: AnimIn; animOut: AnimOut; animMs: number }
  | { kind: 'overlay'; width: number; opacity: number; rotation: number; animIn: AnimIn; animOut: AnimOut; animMs: number };
const EFFECTS = { clip: { id: 'clip', name: 'V1' }, strap: { id: 'strap', name: 'T1' }, vo: { id: 'vo', name: 'A1' }, music: { id: 'music', name: 'A2' }, dip: { id: 'dip', name: 'A2' }, text: { id: 'text', name: 'T2' }, overlay: { id: 'overlay', name: 'V2' }, sound: { id: 'sound', name: 'A3' } };
const TRACK: Record<RowKey, { code: string; icon: string; name: () => string }> = {
  overlay: { code: 'V2', icon: '🖼', name: () => t('veTrackOverlay') },
  video: { code: 'V1', icon: '🎬', name: () => t('veTrackVideo') },
  text: { code: 'T2', icon: 'Aa', name: () => t('veTrackText') },
  straps: { code: 'T1', icon: 'T', name: () => t('veTrackStraps') },
  vo: { code: 'A1', icon: '🎙', name: () => t('veTrackVo') },
  music: { code: 'A2', icon: '♪', name: () => t('veTrackMusic') },
  sound: { code: 'A3', icon: '🔊', name: () => t('veTrackSound') },
};
const LOWER_THIRDS: Array<{ template: StrapTemplate; name: () => string; lines: () => string[] }> = [
  { template: 'lower-third-1', name: () => t('veLt1'), lines: () => [t('veLtSample1')] },
  { template: 'lower-third-2', name: () => t('veLt2'), lines: () => [t('veLtSample1'), t('veLtSample2')] },
];
const TRANSITION_WORDS: Record<TransitionType, () => string> = {
  dissolve: () => t('veTrDissolve'),
  'dip-black': () => t('veTrDipBlack'),
  'dip-white': () => t('veTrDipWhite'),
  wipe: () => t('veTrWipe'),
  push: () => t('veTrPush'),
};
const s1 = (ms: number) => (Math.round(ms / 100) / 10).toFixed(1);
const sizeOf = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`);
const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
const dbLabel = (gain: number) => {
  const db = toDb(gain);
  return db <= -60 ? '−∞ dB' : `${db > 0 ? '+' : ''}${db.toFixed(1)} dB`;
};

const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.readAsDataURL(blob);
  });
/** A media file's length as the browser reads it, or null when it cannot. */
const probeDurationMs = (file: Blob): Promise<number | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    const done = (ms: number | null) => {
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : null);
    v.onerror = () => done(null);
    v.preload = 'metadata';
    v.src = url;
    window.setTimeout(() => done(null), 15_000);
  });

export function VideoEditorPage({ id }: { id: string }) {
  const { query } = useLocation();
  const { can } = useSession();
  // the query object is new on every render: the effect keys on its values
  const storyParam = query.get('story');
  const clipParam = query.get('clip');
  const [edit, setEdit] = useState<EditDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [music, setMusic] = useState<MusicTrack[]>([]);

  // open the edit: an existing one, or the one of (story, clip) made on the spot
  useEffect(() => {
    let cancelled = false;
    const open = async () => {
      try {
        if (id === 'new') {
          if (!storyParam || !clipParam) throw new Error('story και clip');
          const r = await post<{ edit: EditDto }>('/api/edits', { storyId: storyParam, mediaAssetId: clipParam });
          if (!cancelled) navigate(`/edit/${r.edit.id}`, { replace: true });
          return;
        }
        const r = await get<{ edit: EditDto; music: MusicTrack[] }>(`/api/edits/${id}`);
        if (cancelled) return;
        setEdit(r.edit);
        setMusic(r.music);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      }
    };
    const h = window.setTimeout(() => void open(), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(h);
    };
  }, [id, storyParam, clipParam]);

  if (error) return <div className="banner err">{error}</div>;
  if (!edit) return <p className="muted">{t('loading')}</p>;
  return <Editor key={edit.id} initial={edit} music={music} setMusic={setMusic} canEdit={edit.canEdit && can('story.edit.own')} />;
}

function Editor({ initial, music, setMusic, canEdit }: { initial: EditDto; music: MusicTrack[]; setMusic: (m: MusicTrack[]) => void; canEdit: boolean }) {
  const storyId = initial.storyId;
  const [edit, setEdit] = useState(initial);
  const [project, setProjectState] = useState<EditProject>(initial.project);
  // the straps' design: the edit's choice, else its story's bulletin's (ADR 083); what is drawn and sent carries it
  const bulletinDesign = strapDesignFor(edit.programmeName).key;
  const designOf = (p: EditProject): StrapDesignKey => p.strapDesign ?? bulletinDesign;
  const withDesign = (p: EditProject): EditProject => (p.strapDesign ? p : { ...p, strapDesign: bulletinDesign });
  const [library] = useState<LibraryClip[]>(initial.library ?? []);
  const [uploads, setUploads] = useState<Upload[]>(initial.uploads ?? []);
  /** Changes the page holds that the server does not have yet: they are saved by a save, or dropped by leaving without one. */
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved' | 'conflict'>('idle');
  // changes of an earlier visit that never got saved (a closed tab), kept by this browser: offered back once
  const [draft, setDraft] = useState(() => draftToOffer(browserStore(), initial.id, initial.revision, initial.project));
  const [sel, setSel] = useState<Selection>(initial.project.segments[0] ? { kind: 'segment', id: initial.project.segments[0].id } : { kind: 'mix' });
  const [tMs, setTMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [exporting, setExporting] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [voiceovers, setVoiceovers] = useState<Voiceover[]>([]);
  const [ai, setAi] = useState<{ people: Array<{ name: string; role: string }>; captions: string[] } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [snap, setSnap] = useState(true);
  const [tlWidth, setTlWidth] = useState(800);
  const [analyses, setAnalyses] = useState<Record<string, SourceAnalysis>>({});
  const [waves, setWaves] = useState<Record<string, Wave>>({});
  const [codecs, setCodecs] = useState<FastCodecs | null | undefined>(undefined);
  const [binTab, setBinTab] = useState<BinTab>('library');
  const [binQ, setBinQ] = useState('');
  const [dropOver, setDropOver] = useState(false);
  const [hist, setHist] = useState({ past: 0, future: 0 });
  const [aiOpen, setAiOpen] = useState(false);
  const [airOpen, setAirOpen] = useState(false);
  const [pieceTab, setPieceTab] = useState<PieceTab>('basic');
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [fitTop, setFitTop] = useState(0);
  const [stageSize, setStageSize] = useState({ w: 640, h: 360 });
  const [monZoom, setMonZoom] = useState<'fit' | number>('fit');
  const [safe, setSafe] = useState(() => pref('ve-safe', false));
  const [grid, setGrid] = useState(() => pref('ve-grid', false));
  const [guides, setGuides] = useState({ x: false, y: false });
  const [tlH, setTlH] = useState(() => pref('ve-tl-h', 360));
  const [statusOpen, setStatusOpen] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number; sel: Selection } | null>(null);
  const [styleClip, setStyleClip] = useState<StyleClip | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const monitorRef = useRef<HTMLDivElement>(null);
  const headsRef = useRef<HTMLDivElement>(null);
  /** A piece, text or picture being moved, sized or turned on the monitor: where the pointer and the item started. */
  const dragRef = useRef<{ mode: 'move' | 'scale' | 'rotate'; iref: ItemRef; sizeProp: KeyProp; x0: number; y0: number; cx: number; cy: number; v: { x: number; y: number; s: number; r: number }; d0: number; a0: number; moved: boolean } | null>(null);
  const engineRef = useRef<EditEngine | null>(null);
  const meterL = useRef<HTMLSpanElement>(null);
  const meterR = useRef<HTMLSpanElement>(null);
  const tlRef = useRef<TimelineState>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  /** Where the last press on the timeline began: a click that ends further away was a drag or a trim, not a seek. */
  const tlDownRef = useRef({ x: 0, y: 0 });
  const tRef = useRef(0);
  const projectRef = useRef(project);
  const revisionRef = useRef(initial.revision);
  /** This visit saved a change the story has not had yet (Apply, or the way back to the story, gives it). */
  const changedRef = useRef(false);
  const viewRef = useRef({ pxPerS: 1, width: 800, scrollLeft: 0 });
  const historyRef = useRef<{ past: EditProject[]; future: EditProject[]; lastKey: string; lastAt: number }>({ past: [], future: [], lastKey: '', lastAt: 0 });

  // the editor fills the window under the app's header, and the page does not scroll
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => setFitTop(Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY)));
    const raf = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    const html = document.documentElement;
    const before = html.style.overflow;
    html.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      html.style.overflow = before;
    };
  }, []);
  // the monitor's stage, for the picture's size
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setStageSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => savePref('ve-tl-h', tlH), [tlH]);
  // a menu or the status closes on a click elsewhere
  useEffect(() => {
    if (!menu && !statusOpen) return;
    const close = (e: PointerEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest('.ve-menu')) setMenu(null);
      if (!el.closest('.ve-status-wrap')) setStatusOpen(false);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [menu, statusOpen]);

  // the timeline's width, for the zoom that fits the whole sequence
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setTlWidth(el.clientWidth || 800));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── the sources: the pieces' clips and imports, the clips' takes and beds, with their names and lengths ──
  const sourceInfo = useCallback(
    (s: EditSource): { title: string; durationMs: number | null } => {
      if (s.kind === 'clip') {
        const c = library.find((x) => x.id === s.id) ?? (edit.media && edit.media.id === s.id ? { title: edit.media.title, durationMs: edit.media.durationMs } : null);
        return c ? { title: c.title, durationMs: c.durationMs } : { title: s.id, durationMs: null };
      }
      const u = uploads.find((x) => x.id === s.id);
      return u ? { title: u.title, durationMs: u.durationMs } : { title: t('veBinUploads'), durationMs: null };
    },
    [library, uploads, edit.media],
  );
  const audioInfo = useCallback(
    (s: EditAudioSource): { title: string; durationMs: number | null } => {
      if (isPieceSound(s)) return sourceInfo(s);
      const known = waves[audioSourceKey(s)]?.durationMs ?? null;
      if (s.kind === 'voiceover') {
        const v = voiceovers.find((x) => x.id === s.id);
        return { title: v ? (v.kind === 'ai' ? `✦ ${v.voice ?? t('voAi')}` : `🎙 ${v.createdBy ?? t('voRecorded')}`) : t('veVo'), durationMs: known ?? v?.durationMs ?? null };
      }
      const m = music.find((x) => x.id === s.id);
      return { title: m?.title ?? t('veMusic'), durationMs: known ?? m?.durationMs ?? null };
    },
    [voiceovers, music, waves, sourceInfo],
  );
  const durations = useMemo<SourceDurations>(() => ({ video: (s) => sourceInfo(s).durationMs, audio: (s) => audioInfo(s).durationMs }), [sourceInfo, audioInfo]);
  const sources = useMemo(() => {
    const seen = new Map<string, EditSource>();
    for (const s of project.segments) if (s.source.kind !== 'image') seen.set(sourceKey(s.source), s.source);
    // a separated sound keeps its file's strip and waveform, even with its piece gone
    for (const c of project.audio) if (isPieceSound(c.source)) seen.set(audioSourceKey(c.source), c.source);
    return [...seen.entries()].map(([key, source]) => ({ key, source, url: sourceUrlOf(source) }));
  }, [project.segments, project.audio]);
  const audioSources = useMemo(() => {
    const seen = new Map<string, EditAudioSource>();
    // a separated sound's waveform is its file's, from the analysis of the pieces' sources
    for (const c of project.audio) if (!isPieceSound(c.source)) seen.set(audioSourceKey(c.source), c.source);
    return [...seen.entries()].map(([key, source]) => ({ key, source, url: audioUrlOf(source, storyId) }));
  }, [project.audio, storyId]);

  // ── the project, with undo: every change goes through `commit` ──
  const syncHist = () => setHist({ past: historyRef.current.past.length, future: historyRef.current.future.length });
  const commit = useCallback(
    (next: EditProject | ((p: EditProject) => EditProject), key = '') => {
      const prev = projectRef.current;
      const made = clampProject(typeof next === 'function' ? next(prev) : next, durations);
      if (JSON.stringify(made) === JSON.stringify(prev)) return;
      const h = historyRef.current;
      const now = Date.now();
      // a run of the same small change (typing, a slider) is one step of undo
      if (!(key && key === h.lastKey && now - h.lastAt < COALESCE_MS)) {
        h.past.push(prev);
        if (h.past.length > HISTORY_MAX) h.past.shift();
      }
      h.future = [];
      h.lastKey = key;
      h.lastAt = now;
      projectRef.current = made;
      setProjectState(made);
      setDirty(true);
      syncHist();
    },
    [durations],
  );
  const undo = () => {
    const h = historyRef.current;
    const prev = h.past.pop();
    if (!prev) return;
    h.future.push(projectRef.current);
    h.lastKey = '';
    projectRef.current = prev;
    setProjectState(prev);
    setDirty(true);
    syncHist();
  };
  const redo = () => {
    const h = historyRef.current;
    const next = h.future.pop();
    if (!next) return;
    h.past.push(projectRef.current);
    h.lastKey = '';
    projectRef.current = next;
    setProjectState(next);
    setDirty(true);
    syncHist();
  };

  // ── the engine: the monitor's pictures and the mix ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const meter = (el: HTMLSpanElement | null, db: number) => {
      if (!el) return;
      el.style.width = `${clamp(((db + 60) / 60) * 100, 0, 100)}%`;
      el.className = db > -3 ? 'hot' : db > -12 ? 'warm' : '';
    };
    const engine = new EditEngine(
      canvas,
      { video: sourceUrlOf, audio: (s) => audioUrlOf(s, storyId) },
      {
        onTime: (ms) => {
          tRef.current = ms;
          setTMs(ms);
          // keep the playhead in view while playing
          const tl = tlRef.current;
          const { pxPerS, width, scrollLeft } = viewRef.current;
          if (tl && engine.isPlaying) {
            const x = START_LEFT + (ms / 1000) * pxPerS;
            if (x < scrollLeft || x > scrollLeft + width - 24) {
              const left = Math.max(0, x - 40);
              viewRef.current.scrollLeft = left;
              tl.setScrollLeft(left);
            }
          }
        },
        onPlaying: setPlaying,
        onWaiting: setWaiting,
        onDrawn: (b) => setBoxes((prev) => (JSON.stringify(prev) === JSON.stringify(b) ? prev : b)),
        onLevels: (l, r) => {
          meter(meterL.current, l);
          meter(meterR.current, r);
        },
      },
    );
    engineRef.current = engine;
    return () => {
      engine.destroy();
      engineRef.current = null;
    };
  }, [storyId]);
  useEffect(() => {
    engineRef.current?.setProject(project.strapDesign ? project : { ...project, strapDesign: bulletinDesign }, durations);
  }, [project, durations, bulletinDesign]);

  // ── unsaved changes (ADR 085): the server has the edit as last saved, so the story, its video and the render for
  // air stay as they were until a save; this browser keeps a copy a moment after the last change, for a closed tab ──
  useEffect(() => {
    if (!dirty || !canEdit) return;
    const h = window.setTimeout(() => keepDraft(browserStore(), edit.id, { base: revisionRef.current, at: new Date().toISOString(), project }), 600);
    return () => window.clearTimeout(h);
  }, [project, dirty, canEdit, edit.id]);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);
  // a link of the app away from the editor asks first, and leaving drops the changes
  useEffect(() => {
    if (!dirty || !canEdit) return;
    setLeaveGuard(() => {
      if (!window.confirm(t('veLeaveConfirm'))) return false;
      dropDraft(browserStore(), edit.id);
      return true;
    });
    return () => setLeaveGuard(null);
  }, [dirty, canEdit, edit.id]);

  // ── the voice-overs of the story ──
  const loadVo = useCallback(async () => {
    try {
      const r = await get<{ voiceovers: Voiceover[] }>(`/api/stories/${storyId}/voiceovers`);
      setVoiceovers(r.voiceovers);
    } catch {
      /* the panel reports */
    }
  }, [storyId]);
  useEffect(() => {
    const h = window.setTimeout(() => void loadVo(), 0);
    return () => window.clearTimeout(h);
  }, [loadVo]);

  // ── what the timeline shows of the media: filmstrips and waveforms, and what the browser can encode ──
  useEffect(() => {
    let on = true;
    fastExportCodecs().then((c) => on && setCodecs(c));
    return () => {
      on = false;
    };
  }, []);
  const sourceKeys = sources.map((s) => s.key).join('|');
  useEffect(() => {
    let on = true;
    for (const s of sources) {
      analyseSource(s.url)
        .then((a) => on && setAnalyses((prev) => (prev[s.key] ? prev : { ...prev, [s.key]: a })))
        // without the strip the timeline still works; the label must not say "analysing" forever
        .catch(() => on && setAnalyses((prev) => (prev[s.key] ? prev : { ...prev, [s.key]: { durationS: 0, thumbs: [], wave: null } })));
    }
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the keys name the sources
  }, [sourceKeys]);
  const audioKeys = audioSources.map((s) => s.key).join('|');
  useEffect(() => {
    let on = true;
    for (const s of audioSources) {
      analyseSound(s.url, s.source.kind === 'voiceover' ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.6)')
        .then((r) => on && setWaves((prev) => (prev[s.key] ? prev : { ...prev, [s.key]: r })))
        .catch(() => undefined);
    }
    return () => {
      on = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the keys name the sources
  }, [audioKeys]);

  const seek = useCallback((ms: number) => {
    engineRef.current?.seek(ms);
  }, []);
  const pressMoved = (e: { clientX: number; clientY: number }) => pressTravelled(tlDownRef.current, { x: e.clientX, y: e.clientY });
  useEffect(() => {
    const tl = tlRef.current;
    if (!tl) return;
    const s = tMs / 1000;
    if (Math.abs(tl.getTime() - s) > 0.004) tl.setTime(s);
  }, [tMs]);

  // ── the zoom of the ruler ──
  const totalMs = editDurationMs(project);
  const totalS = totalMs / 1000;
  const fitIdx = fitScaleIndex(totalS, tlWidth);
  const zoomIdx = clamp(zoom ?? fitIdx, 0, SCALES.length - 1);
  const scale = SCALES[zoomIdx]!;
  const pxPerS = pxPerSecond(scale);
  const scaleCount = Math.ceil(totalS / scale) + 2;
  useEffect(() => {
    viewRef.current.pxPerS = pxPerS;
    viewRef.current.width = tlWidth;
  }, [pxPerS, tlWidth]);

  // ── pieces ──
  const selectedSegment = sel.kind === 'segment' ? project.segments.find((s) => s.id === sel.id) : undefined;
  const selectedAudio = sel.kind === 'audio' ? project.audio.find((c) => c.id === sel.id) : undefined;
  /** Only what the bins hold goes on the timeline (a stray drop names nothing the API would accept). */
  const known = (s: EditSource) => (s.kind === 'clip' ? library.some((c) => c.id === s.id) || edit.media?.id === s.id : uploads.some((u) => u.id === s.id && isImageUpload(u) === (s.kind === 'image')));
  const videoUploads = uploads.filter((u) => !isImageUpload(u));
  const imageUploads = uploads.filter(isImageUpload);
  const addSource = (source: EditSource, durationMs: number | null, atMs?: number) => {
    if (!canEdit || !known(source)) return;
    const piece = makeSegment(source, 0, Math.max(1000, Math.round(durationMs ?? 5000)));
    commit((p) => insertSegment(p, piece, atMs ?? editDurationMs(p)));
    setSel({ kind: 'segment', id: piece.id });
  };
  const removePiece = (id: string) => {
    if (!canEdit) return;
    const p = projectRef.current;
    if (p.segments.length <= 1) return;
    const idx = p.segments.findIndex((s) => s.id === id);
    const neighbour = p.segments[idx + 1] ?? p.segments[idx - 1];
    commit((q) => removeSegment(q, id));
    setSel(neighbour ? { kind: 'segment', id: neighbour.id } : { kind: 'mix' });
  };
  const movePiece = (id: string, delta: number) => {
    const idx = projectRef.current.segments.findIndex((s) => s.id === id);
    if (idx < 0) return;
    commit((p) => moveSegment(p, id, idx + delta));
  };
  const patchPiece = (id: string, patch: Partial<EditSegment>, key = '') => commit((p) => ({ ...p, segments: p.segments.map((s) => (s.id === id ? { ...s, ...patch } : s)) }), key);
  /** IN here: the piece under the playhead starts here (what is before goes). OUT here: it ends here. */
  const setIn = () => {
    if (!canEdit) return;
    const hit = segmentAt(projectRef.current, tRef.current);
    if (!hit) return;
    patchPiece(hit.segment.id, { inMs: Math.min(Math.round(hit.sourceMs), hit.segment.outMs - 40) });
    setSel({ kind: 'segment', id: hit.segment.id });
  };
  const setOut = () => {
    if (!canEdit) return;
    const hit = segmentAt(projectRef.current, tRef.current);
    if (!hit) return;
    patchPiece(hit.segment.id, { outMs: Math.max(Math.round(hit.sourceMs), hit.segment.inMs + 40) });
    setSel({ kind: 'segment', id: hit.segment.id });
  };

  // ── cutting: C on the selected track, Shift+C on every track ──
  const cut = (all = false) => {
    if (!canEdit) return;
    const p = projectRef.current;
    const at = tRef.current;
    if (all) {
      commit((q) => splitAllAt(q, at));
      return;
    }
    const row = rowOfSelection(sel, p) ?? 'video';
    if (row === 'vo' || row === 'music') {
      const under = audioAt(p, row, at);
      const target = (selectedAudio && under.find((c) => c.id === selectedAudio.id)) ?? under[0];
      if (target) {
        commit((q) => splitAudioAt(q, target.id, at));
        setSel({ kind: 'audio', id: target.id });
      } else setNotice(t('veNothingToCut'));
      return;
    }
    if (row === 'text') {
      const under = p.texts.filter((x) => at > x.atMs && at < x.atMs + x.durationMs);
      const target = (sel.kind === 'text' && under.find((x) => x.id === sel.id)) || under[0];
      if (target) commit((q) => splitTextAt(q, target.id, at));
      else setNotice(t('veNothingToCut'));
      return;
    }
    const hit = segmentAt(p, at);
    commit((q) => splitAt(q, at));
    if (hit) setSel({ kind: 'segment', id: hit.segment.id });
  };

  // ── photos, texts and pictures over the sequence ──
  const addPhoto = (imageId: string, atMs?: number) => {
    if (!canEdit || !known({ kind: 'image', id: imageId })) return;
    const piece = makeSegment({ kind: 'image', id: imageId }, 0, DEFAULT_PHOTO_MS);
    commit((p) => insertSegment(p, piece, atMs ?? editDurationMs(p)));
    setSel({ kind: 'segment', id: piece.id });
  };
  const addOverlay = (imageId: string, atMs = tRef.current) => {
    if (!canEdit || !known({ kind: 'image', id: imageId })) return;
    const o = makeOverlay(imageId, atMs, DEFAULT_OVERLAY_MS);
    commit((p) => ({ ...p, overlays: [...p.overlays, o] }));
    setSel({ kind: 'overlay', id: o.id });
    // the playhead just past its animation in, so the new picture shows
    seek(o.atMs + Math.min(o.animMs, o.durationMs / 2) + FRAME_MS);
  };
  const patchOverlay = (id: string, patch: Partial<EditOverlay>, key = '') => commit((p) => ({ ...p, overlays: p.overlays.map((o) => (o.id === id ? { ...o, ...patch } : o)) }), key);
  const removeOverlay = (id: string) => {
    commit((p) => ({ ...p, overlays: p.overlays.filter((o) => o.id !== id) }));
    setSel({ kind: 'overlays' });
  };
  const addText = (presetId = 'title', atMs = tRef.current) => {
    if (!canEdit) return;
    const pr = presetById(presetId);
    const x = makeText(atMs, t(pr.sample), pr.style, { animIn: pr.animIn, animOut: pr.animOut, animMs: pr.animMs ?? 400 });
    commit((p) => ({ ...p, texts: [...p.texts, x] }));
    setSel({ kind: 'text', id: x.id });
    // the playhead just past its animation in (all of it typed), so the new text shows
    seek(x.atMs + (x.animIn === 'typewriter' ? Math.min(x.durationMs * 0.8, Math.max(x.animMs, 1500)) : Math.min(x.animMs, x.durationMs / 2)) + FRAME_MS);
  };
  const patchText = (id: string, patch: Partial<EditText>, key = '') => commit((p) => ({ ...p, texts: p.texts.map((x) => (x.id === id ? { ...x, ...patch } : x)) }), key);
  const removeText = (id: string) => {
    commit((p) => ({ ...p, texts: p.texts.filter((x) => x.id !== id) }));
    setSel({ kind: 'texts' });
  };
  /** Captions from the script of each voice-over clip on A1, spread by length; the ones made before go. */
  const autoCaptions = () => {
    if (!canEdit) return;
    const p = projectRef.current;
    const pr = presetById('caption');
    const made: EditText[] = [];
    for (const c of p.audio) {
      if (c.track !== 'vo' || c.source.kind !== 'voiceover') continue;
      const take = voiceovers.find((v) => v.id === c.source.id);
      const len = audioInfo(c.source).durationMs;
      if (!take || !take.text.trim() || !len) continue;
      for (const cap of captionsForClip(c, take.text, len)) made.push(makeText(cap.atMs, cap.text, pr.style, { durationMs: cap.durationMs, animIn: 'fade', animOut: 'fade', animMs: 120, caption: true }));
    }
    if (!made.length) {
      setNotice(t('veNoCaptions'));
      return;
    }
    commit((q) => ({ ...q, texts: [...q.texts.filter((x) => !x.caption), ...made] }));
    setSel({ kind: 'texts' });
    setNotice(`${made.length} ${t('veCaptionsMade')}`);
  };
  const clearCaptions = () => commit((p) => ({ ...p, texts: p.texts.filter((x) => !x.caption) }));
  /** Every caption in the look and place of this one. */
  const styleAllCaptions = (from: EditText) => commit((p) => ({ ...p, texts: p.texts.map((x) => (x.caption ? { ...x, style: { ...from.style }, animIn: from.animIn, animOut: from.animOut, animMs: from.animMs } : x)) }));

  // ── a piece's look, move, effects and speed from the bins ──
  /** The piece a card applies to: the one dropped on, else the selected one, else the one under the playhead. */
  const targetPiece = (atMs?: number): EditSegment | null => {
    const p = projectRef.current;
    if (atMs === undefined && sel.kind === 'segment') return p.segments.find((s) => s.id === sel.id) ?? null;
    return segmentAt(p, atMs ?? tRef.current)?.segment ?? null;
  };
  const applyFilter = (filter: FilterType, atMs?: number) => {
    const seg = targetPiece(atMs);
    if (!canEdit || !seg) return;
    patchPiece(seg.id, { look: { ...seg.look, filter, amount: 1 } });
    setSel({ kind: 'segment', id: seg.id });
    setPieceTab('color');
  };
  const addEffect = (type: EffectType, atMs?: number) => {
    const seg = targetPiece(atMs);
    if (!canEdit || !seg) return;
    if (!seg.effects.some((e) => e.type === type)) patchPiece(seg.id, { effects: [...seg.effects, { type, amount: 0.5 }] });
    setSel({ kind: 'segment', id: seg.id });
    setPieceTab('effects');
  };
  const setMotion = (type: MotionType, atMs?: number) => {
    const seg = targetPiece(atMs);
    if (!canEdit || !seg) return;
    patchPiece(seg.id, { motion: { type, amount: seg.motion.amount } });
    setSel({ kind: 'segment', id: seg.id });
    setPieceTab('frame');
  };
  /** The frame under the playhead held for two seconds between the halves of its piece. */
  const freeze = () => {
    if (!canEdit) return;
    const r = freezeAt(projectRef.current, tRef.current);
    if (!r.segmentId) {
      setNotice(t('veFreezeNone'));
      return;
    }
    commit(r.project);
    setSel({ kind: 'segment', id: r.segmentId });
    setPieceTab('speed');
  };
  /**
   * L: the selected piece's sound separated from its picture onto A3, to move, cut or delete on its own; on a piece
   * already separated, or on its sound on A3, the sound goes back into its piece.
   */
  const toggleSoundApart = (x: Selection = sel) => {
    if (!canEdit) return;
    const p = projectRef.current;
    const link = x.kind === 'segment' ? p.segments.find((s) => s.id === x.id)?.audioLink : x.kind === 'audio' ? p.audio.find((c) => c.id === x.id)?.link : undefined;
    if (link) {
      const piece = p.segments.find((s) => s.audioLink === link);
      commit(relinkSound(p, link));
      if (piece) setSel({ kind: 'segment', id: piece.id });
      setNotice(t('veSoundRelinked'));
      return;
    }
    const seg = x.kind === 'segment' ? p.segments.find((s) => s.id === x.id) : undefined;
    if (!seg) return setNotice(t('veSoundPickPiece'));
    if (!pieceSounds(seg)) return setNotice(t('veSoundNone'));
    if (seg.speed !== 1) return setNotice(t('veSoundSpeed'));
    const r = detachSound(p, seg.id);
    if (!r) return;
    commit(r.project);
    setSel({ kind: 'audio', id: r.clipId });
    setNotice(t('veSoundDetached'));
    // A3 is the last track: scrolled into view, under the pieces
    window.setTimeout(() => tlRef.current?.setScrollTop(ROW_KEYS.reduce((a, k) => a + ROW_HEIGHT[k], 0)), 0);
  };
  const patchFormat = (patch: Partial<EditFormat>, key = '') => commit((p) => ({ ...p, format: { ...p.format, ...patch } }), key);
  const patchOutput = (patch: Partial<EditOutput>, key = '') => commit((p) => ({ ...p, output: { ...p.output, ...patch } }), key);

  // ── transitions ──
  const putTransition = (type: TransitionType | null, atMs = tRef.current) => {
    if (!canEdit) return;
    const r = setTransitionNear(projectRef.current, atMs, type ? { type, durationMs: DEFAULT_TRANSITION_MS } : null);
    commit(r.project);
    if (r.segmentId) setSel({ kind: 'segment', id: r.segmentId });
  };

  // ── straps ──
  const addStrap = (lines: string[], atMs = tRef.current, template?: StrapTemplate) => {
    if (!canEdit) return;
    const s: EditStrap = { id: strapId(), atMs: Math.round(atMs), durationMs: DEFAULT_STRAP_MS, template: template ?? (lines.length >= 2 ? 'lower-third-2' : 'lower-third-1'), lines: template === 'lower-third-2' && lines.length < 2 ? [lines[0] ?? '', ''] : lines, ...STRAP_ANIM };
    commit((p) => ({ ...p, straps: [...p.straps, s] }));
    setSel({ kind: 'strap', id: s.id });
  };
  const patchStrap = (id: string, patch: Partial<EditStrap>, key = '') => commit((p) => ({ ...p, straps: p.straps.map((s) => (s.id === id ? { ...s, ...patch } : s)) }), key);
  const removeStrap = (id: string) => {
    commit((p) => ({ ...p, straps: p.straps.filter((s) => s.id !== id) }));
    setSel({ kind: 'straps' });
  };

  // ── audio clips ──
  const addAudio = async (source: EditAudioSource, track: AudioTrack, durationMs: number | null, atMs = tRef.current) => {
    if (!canEdit) return;
    let len = durationMs;
    if (!len) {
      try {
        len = (await analyseSound(audioUrlOf(source, storyId), 'rgba(255,255,255,0.7)')).durationMs;
      } catch {
        len = 5000;
      }
    }
    const c: EditAudioClip = { id: audioClipId(), track, source, atMs: Math.round(atMs), inMs: 0, outMs: Math.max(100, Math.round(len)), gain: track === 'music' ? 0.35 : 1, fadeInMs: track === 'music' ? 500 : 0, fadeOutMs: track === 'music' ? 1000 : 0 };
    commit((p) => ({ ...p, audio: [...p.audio, c] }));
    setSel({ kind: 'audio', id: c.id });
  };
  const patchAudio = (id: string, patch: Partial<EditAudioClip>, key = '') => commit((p) => ({ ...p, audio: p.audio.map((c) => (c.id === id ? { ...c, ...patch } : c)) }), key);
  const removeAudio = (id: string) => {
    const c = projectRef.current.audio.find((x) => x.id === id);
    commit((p) => ({ ...p, audio: p.audio.filter((x) => x.id !== id) }));
    setSel(c ? { kind: 'track', track: c.track } : { kind: 'mix' });
  };
  /** The clip's loudest peak brought to −1 dBFS (a level up to +6 dB). */
  const normalise = (c: EditAudioClip) => {
    const buffer = engineRef.current?.bufferOf(c.source);
    if (!buffer) {
      setNotice(t('veNormaliseWait'));
      return;
    }
    const peak = peakOf(buffer, c.inMs / 1000, c.outMs / 1000);
    if (peak <= 0.0001) return;
    patchAudio(c.id, { gain: clamp(0.891 / peak, 0, MAX_GAIN) });
  };

  // ── dips: sections where the music drops ──
  const addDip = (atMs = tRef.current) => {
    if (!canEdit || !projectRef.current.audio.some((c) => c.track === 'music')) return;
    const d: EditDip = { id: dipId(), fromMs: Math.round(atMs), toMs: Math.round(atMs) + DIP_MS, level: DIP_LEVEL };
    commit((p) => ({ ...p, dips: [...p.dips, d] }));
    setSel({ kind: 'dip', id: d.id });
  };
  const patchDip = (id: string, patch: Partial<EditDip>, key = '') => commit((p) => ({ ...p, dips: p.dips.map((d) => (d.id === id ? { ...d, ...patch } : d)) }), key);
  const removeDip = (id: string) => {
    commit((p) => ({ ...p, dips: p.dips.filter((d) => d.id !== id) }));
    setSel({ kind: 'track', track: 'music' });
  };

  // ── the mix ──
  const patchTrack = (track: 'video' | 'vo' | 'music' | 'sound', patch: Partial<EditProject['mix']['music']>, key = '') => commit((p) => ({ ...p, mix: { ...p.mix, [track]: { ...p.mix[track], ...patch } } }), key);

  // ── keys ──
  const onKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    const mod = e.ctrlKey || e.metaKey;
    // the letters by their place on the keyboard too: on the Greek layout L types λ
    const k = letterKey(e);
    if (mod && k === 'z') {
      if (e.shiftKey) redo();
      else undo();
    } else if (mod && k === 'y') redo();
    else if (mod && k === 'd') duplicate(sel);
    else if (mod && k === 'c') copyStyle(sel);
    else if (mod && k === 'v') pasteStyle(sel);
    else if (k === 'k') {
      if (selRef) toggleAllKeys(selRef);
    } else if (k === 'Escape') setMenu(null);
    else if (k === ' ') engineRef.current?.toggle();
    else if (k === 'ArrowLeft') seek(tRef.current - (e.shiftKey ? 1000 : FRAME_MS));
    else if (k === 'ArrowRight') seek(tRef.current + (e.shiftKey ? 1000 : FRAME_MS));
    else if (k === 'Home') seek(0);
    else if (k === 'End') seek(editDurationMs(projectRef.current));
    else if (k === 'c') cut(e.shiftKey);
    else if (k === 'd') addDip();
    else if (k === 't') putTransition('dissolve');
    else if (k === 'f') freeze();
    else if (k === 'i') setIn();
    else if (k === 'o') setOut();
    else if (k === 's') setSnap((x) => !x);
    else if (k === '+' || k === '=') setZoom(Math.min(SCALES.length - 1, zoomIdx + 1));
    else if (k === '-' || k === '_') setZoom(Math.max(0, zoomIdx - 1));
    else if (k === 'l') toggleSoundApart();
    else if (k === 'Delete' || k === 'Backspace') {
      if (sel.kind === 'strap') removeStrap(sel.id);
      else if (sel.kind === 'segment') removePiece(sel.id);
      else if (sel.kind === 'audio') removeAudio(sel.id);
      else if (sel.kind === 'dip') removeDip(sel.id);
      else if (sel.kind === 'text') removeText(sel.id);
      else if (sel.kind === 'overlay') removeOverlay(sel.id);
      else return;
    } else return;
    e.preventDefault();
  };

  // ── export, apply, AI, music, import ──
  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  /** The page's unsaved changes, saved to the edit now (the browser's copy goes); the saved edit, or null when there were none. */
  const flush = async (): Promise<EditDto | null> => {
    if (!dirty) return null;
    setDirty(false);
    setSaving('saving');
    try {
      const r = await put<{ edit: EditDto }>(`/api/edits/${edit.id}`, { project: projectRef.current, revision: revisionRef.current });
      revisionRef.current = r.edit.revision;
      changedRef.current = true;
      dropDraft(browserStore(), edit.id);
      setEdit((e) => ({ ...e, ...r.edit, uploads: e.uploads, library: e.library }));
      setSaving('saved');
      return r.edit;
    } catch (e) {
      setDirty(true);
      setSaving(e instanceof ApiError && e.code === 'CONFLICT' ? 'conflict' : 'idle');
      throw e;
    }
  };
  /** The project rendered to a file and kept as the edit's export; true when the fast export failed and the page recorded it. */
  const renderOutput = async (): Promise<boolean> => {
    engineRef.current?.pause();
    setExporting(0);
    try {
      const p = withDesign(projectRef.current);
      const realtime = async (): Promise<RenderResult> => {
        if (!engineRef.current) throw new Error(t('veNoRecorder'));
        return engineRef.current.record(setExporting);
      };
      let out: RenderResult;
      let fellBack = false;
      if (codecs) {
        try {
          out = await exportEdit({ project: p, durations, audioUrl: (s) => audioUrlOf(s, storyId), codecs, onProgress: setExporting });
        } catch (e) {
          console.warn('fast export failed; recording in real time', e);
          fellBack = true;
          setExporting(0);
          out = await realtime();
        }
      } else out = await realtime();
      setExporting(1);
      const r = await put<{ edit: EditDto }>(`/api/edits/${edit.id}/output`, { video: await toBase64(out.blob), mime: out.mime, durationMs: out.durationMs, revision: revisionRef.current });
      setEdit((e) => ({ ...e, ...r.edit, uploads: e.uploads, library: e.library }));
      return fellBack;
    } finally {
      setExporting(null);
      seek(0);
    }
  };
  // the render for air on the playout, from its masters: saved first, then drawn and sent by the dialog
  const air = useAir(edit.id);
  const prepareAir = async () => {
    await flush();
    return { project: withDesign(projectRef.current), revision: revisionRef.current };
  };
  const exportNow = () =>
    run('export', async () => {
      await flush();
      // a browser that cannot write video still saves the changes
      if (!canExport) return setNotice(t('veSaved'));
      const fellBack = await renderOutput();
      setNotice(fellBack ? `${t('veExported')} · ${t('veExportFellBack')}` : t('veExported'));
    });
  /** Back to the story with the edit as it was last saved: the page's changes, and the browser's copy of them, go. */
  const leaveUnsaved = () => {
    if (dirty && !window.confirm(t('veLeaveConfirm'))) return;
    dropDraft(browserStore(), edit.id);
    setLeaveGuard(null);
    engineRef.current?.pause();
    navigate(`/story/${edit.storyId}/edit`);
  };
  const restoreDraft = () => {
    if (!draft) return;
    commit(draft.project);
    setDraft(null);
  };
  const discardDraft = () => {
    dropDraft(browserStore(), edit.id);
    setDraft(null);
  };
  const apply = () =>
    run('apply', async () => {
      await flush();
      const r = await post<{ mode: 'single' | 'sequence' }>(`/api/edits/${edit.id}/apply`, {});
      changedRef.current = false;
      setNotice(r.mode === 'sequence' ? t('veAppliedSequence') : t('veApplied'));
    });
  /**
   * Back to the story, which then shows the edit as it is now. The page's unsaved changes are saved ("Έξοδος χωρίς
   * αποθήκευση" is the way back without them); when this visit changed the edit, it goes into the story as Apply puts it, exported first
   * when the last change has no export yet: the story airs that file (anything but a plain cut) and its preview plays
   * it. A plain cut airs as the clip under the story's titles, so its export is only the preview: it is made only by
   * the fast export, and a failed one does not stop the apply. A visit that changed nothing just goes back; so does a
   * second press after the export or the apply failed.
   */
  const toStory = () =>
    run('story', async () => {
      const saved = canEdit ? await flush() : null;
      if (canEdit && changedRef.current) {
        try {
          const rendered = saved ? saved.rendered : edit.rendered;
          const plain = isPlainCut(projectRef.current, edit.mediaAssetId);
          if (!rendered && (codecs || (!plain && renderMime()))) {
            try {
              await renderOutput();
            } catch (e) {
              if (!plain) throw e;
              console.warn('the export for the preview failed; the plain cut goes into the story without it', e);
            }
          }
          await post(`/api/edits/${edit.id}/apply`, {});
          changedRef.current = false;
        } catch (e) {
          changedRef.current = false;
          throw new Error(`${e instanceof Error ? e.message : String(e)} · ${t('veToStoryAnyway')}`);
        }
      }
      navigate(`/story/${edit.storyId}/edit`);
    });
  const suggest = () =>
    run('ai', async () => {
      const r = await post<{ people?: Array<{ name: string; role: string }>; captions?: string[] }>('/api/ai/assist', { task: 'lowerThirds', text: edit.storyText || edit.storyTitle, title: edit.storyTitle });
      setAi({ people: r.people ?? [], captions: r.captions ?? [] });
    });
  const uploadMusic = (file: File) =>
    run('music', async () => {
      const durationMs = await probeDurationMs(file);
      const r = await post<{ id: string; tracks: MusicTrack[] }>('/api/music', { title: file.name.replace(/\.[^.]+$/, '').slice(0, 200), audio: await toBase64(file), mime: file.type || 'audio/mpeg', durationMs });
      setMusic(r.tracks);
      await addAudio({ kind: 'music', id: r.id }, 'music', durationMs, 0);
    });
  const importFile = (file: File) =>
    run('import', async () => {
      if (file.size > MAX_IMPORT_BYTES) throw new Error(t('veImportTooBig'));
      const durationMs = await probeDurationMs(file);
      const r = await post<{ upload: Upload; uploads: Upload[] }>(`/api/edits/${edit.id}/uploads`, { filename: file.name.slice(0, 200), video: await toBase64(file), mime: file.type || 'video/mp4', durationMs });
      setUploads(r.uploads);
      setBinTab('uploads');
      const piece = makeSegment({ kind: 'upload', id: r.upload.id }, 0, Math.max(1000, Math.round(r.upload.durationMs ?? durationMs ?? 5000)));
      commit((p) => insertSegment(p, piece, editDurationMs(p)));
      setSel({ kind: 'segment', id: piece.id });
      setNotice(t('veImported'));
    });
  const importImage = (file: File) =>
    run('import', async () => {
      if (file.size > MAX_IMAGE_BYTES) throw new Error(t('veImageTooBig'));
      const r = await post<{ upload: Upload; uploads: Upload[] }>(`/api/edits/${edit.id}/uploads`, { filename: file.name.slice(0, 200), video: await toBase64(file), mime: file.type || 'image/png' });
      setUploads(r.uploads);
      setBinTab('images');
      setNotice(t('veImageImported'));
    });
  const removeUpload = (id: string) =>
    run('import', async () => {
      const r = await del<{ uploads: Upload[] }>(`/api/uploads/${id}`);
      setUploads(r.uploads);
      commit((p) => {
        const segments = p.segments.filter((s) => !(s.source.kind !== 'clip' && s.source.id === id));
        return { ...p, segments: segments.length ? segments : p.segments, overlays: p.overlays.filter((o) => o.source.id !== id) };
      });
    });
  /** The AI editor's edit, applied as one change the journalist can undo. */
  const applyAiEdit = (p: EditProject, notes: string) => {
    engineRef.current?.pause();
    commit(p, 'ai-edit');
    historyRef.current.lastKey = '';
    seek(0);
    setSel(p.segments[0] ? { kind: 'segment', id: p.segments[0].id } : { kind: 'mix' });
    setNotice(`✦ ${t('veAiDone')}${notes ? ` ${notes}` : ''}`);
    setAiOpen(false);
  };

  // ── dragging from the bins onto the timeline ──
  const dragStart = (e: React.DragEvent, payload: DragPayload) => {
    e.dataTransfer.setData(DND, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'copy';
    (e.currentTarget as HTMLElement).classList.add('dragging');
  };
  const dragEnd = (e: React.DragEvent) => (e.currentTarget as HTMLElement).classList.remove('dragging');
  const timeAtX = (clientX: number) => {
    const el = wrapRef.current;
    if (!el) return tRef.current;
    const x = clientX - el.getBoundingClientRect().left - START_LEFT + viewRef.current.scrollLeft;
    return Math.max(0, (x / pxPerS) * 1000);
  };
  /** The track under a point of the timeline (null on the ruler or below the tracks). */
  const rowAtY = (clientY: number): RowKey | null => {
    const el = wrapRef.current;
    if (!el) return null;
    let y = clientY - el.getBoundingClientRect().top - RULER_PX;
    if (y < 0) return null;
    for (const k of ROW_KEYS) {
      if (y < ROW_HEIGHT[k]) return k;
      y -= ROW_HEIGHT[k];
    }
    return null;
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!canEdit || !e.dataTransfer.types.includes(DND)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dropOver) setDropOver(true);
  };
  const onDrop = (e: React.DragEvent) => {
    const raw = e.dataTransfer.getData(DND);
    setDropOver(false);
    if (!raw || !canEdit) return;
    e.preventDefault();
    const d = JSON.parse(raw) as DragPayload;
    const atMs = timeAtX(e.clientX);
    if (d.kind === 'image') {
      if (rowAtY(e.clientY) === 'overlay') addOverlay(d.id, atMs);
      else addPhoto(d.id, atMs);
    } else if (d.kind === 'text') addText(d.preset, atMs);
    else if (d.kind === 'filter') applyFilter(d.filter, atMs);
    else if (d.kind === 'effect') addEffect(d.type, atMs);
    else if (d.kind === 'motion') setMotion(d.type, atMs);
    else if (d.kind === 'strap') addStrap([''], atMs, d.template);
    else if (d.kind === 'take') void addAudio({ kind: 'voiceover', id: d.id }, 'vo', d.durationMs, atMs);
    else if (d.kind === 'bed') void addAudio({ kind: 'music', id: d.id }, 'music', d.durationMs, atMs);
    else if (d.kind === 'transition') putTransition(d.type, atMs);
    else if (d.kind === 'endfade') commit((p) => ({ ...p, endFade: { color: d.color, durationMs: 1000 } }));
    else addSource({ kind: d.kind, id: d.id }, d.durationMs, atMs);
  };

  /** A card dropped on the monitor: for the picture under the playhead (a photo goes over it). */
  const onMonitorDrop = (e: React.DragEvent) => {
    const raw = e.dataTransfer.getData(DND);
    if (!raw || !canEdit) return;
    e.preventDefault();
    const d = JSON.parse(raw) as DragPayload;
    if (d.kind === 'image') addOverlay(d.id);
    else if (d.kind === 'text') addText(d.preset);
    else if (d.kind === 'filter') applyFilter(d.filter, tRef.current);
    else if (d.kind === 'effect') addEffect(d.type, tRef.current);
    else if (d.kind === 'motion') setMotion(d.type, tRef.current);
  };

  // ── keyframes: an item's values at the playhead; where a value has keyframes, a change sets one there ──
  const itemInfo = (p: EditProject, iref: ItemRef): ItemInfo | null => {
    if (iref.kind === 'segment') {
      const i = p.segments.findIndex((x) => x.id === iref.id);
      const sg = p.segments[i];
      if (!sg) return null;
      const tr = sg.transform;
      return { start: segmentStarts(p)[i]!, length: segmentLengthMs(sg), keys: sg.keys, base: { x: tr.x, y: tr.y, scale: tr.scale, rotation: tr.rotation, opacity: tr.opacity } };
    }
    if (iref.kind === 'text') {
      const x = p.texts.find((it) => it.id === iref.id);
      if (!x) return null;
      const st = x.style;
      return { start: x.atMs, length: x.durationMs, keys: x.keys, base: { x: st.x, y: st.y, size: st.size, rotation: st.rotation, opacity: st.opacity } };
    }
    const o = p.overlays.find((it) => it.id === iref.id);
    return o ? { start: o.atMs, length: o.durationMs, keys: o.keys, base: { x: o.x, y: o.y, width: o.width, rotation: o.rotation, opacity: o.opacity } } : null;
  };
  /** The item with its keyframes and its own values replaced. */
  const withItem = (p: EditProject, iref: ItemRef, keys: EditKeys, b: Partial<Record<KeyProp, number>>): EditProject => {
    if (iref.kind === 'segment')
      return { ...p, segments: p.segments.map((x) => (x.id === iref.id ? { ...x, keys, transform: { ...x.transform, x: b.x ?? x.transform.x, y: b.y ?? x.transform.y, scale: b.scale ?? x.transform.scale, rotation: b.rotation ?? x.transform.rotation, opacity: b.opacity ?? x.transform.opacity } } : x)) };
    if (iref.kind === 'text')
      return { ...p, texts: p.texts.map((x) => (x.id === iref.id ? { ...x, keys, style: { ...x.style, x: b.x ?? x.style.x, y: b.y ?? x.style.y, size: b.size ?? x.style.size, rotation: b.rotation ?? x.style.rotation, opacity: b.opacity ?? x.style.opacity } } : x)) };
    return { ...p, overlays: p.overlays.map((x) => (x.id === iref.id ? { ...x, keys, x: b.x ?? x.x, y: b.y ?? x.y, width: b.width ?? x.width, rotation: b.rotation ?? x.rotation, opacity: b.opacity ?? x.opacity } : x)) };
  };
  const localIn = (info: ItemInfo, tAbs: number) => clamp(tAbs - info.start, 0, info.length);
  const valueAt = (p: EditProject, iref: ItemRef, prop: KeyProp, tAbs: number): number => {
    const info = itemInfo(p, iref);
    return info ? keyValue(info.keys[prop], localIn(info, tAbs), info.base[prop] ?? 0) : 0;
  };
  /** Values of an item at the playhead: a keyframe where the value has keyframes, its own value where it has none. */
  const setValues = (iref: ItemRef, values: Partial<Record<KeyProp, number>>, key = '') =>
    commit((p) => {
      const info = itemInfo(p, iref);
      if (!info) return p;
      const local = localIn(info, tRef.current);
      let keys = info.keys;
      const base = { ...info.base };
      for (const [prop, v] of Object.entries(values) as Array<[KeyProp, number]>) {
        if (keys[prop]?.length) keys = setKey(keys, prop, local, v);
        else base[prop] = v;
      }
      return withItem(p, iref, keys, base);
    }, key);
  /** A value's keyframe at the playhead, added (at the value as it is now) or taken away. */
  const toggleKey = (iref: ItemRef, prop: KeyProp) =>
    commit((p) => {
      const info = itemInfo(p, iref);
      if (!info) return p;
      const local = localIn(info, tRef.current);
      const keys = keyIndexAt(info.keys, prop, local) >= 0 ? removeKey(info.keys, prop, local) : setKey(info.keys, prop, local, keyValue(info.keys[prop], local, info.base[prop] ?? 0));
      return withItem(p, iref, keys, info.base);
    });
  /** Keyframes of every value of the item at the playhead, or none there when it has some (K, ◆). */
  const toggleAllKeys = (iref: ItemRef) =>
    commit((p) => {
      const info = itemInfo(p, iref);
      if (!info) return p;
      const local = localIn(info, tRef.current);
      const props = KEY_PROPS_OF[iref.kind] as readonly KeyProp[];
      const any = props.some((pr) => keyIndexAt(info.keys, pr, local) >= 0);
      let keys = info.keys;
      for (const pr of props) keys = any ? removeKey(keys, pr, local) : setKey(keys, pr, local, keyValue(info.keys[pr], local, info.base[pr] ?? 0));
      return withItem(p, iref, keys, info.base);
    });
  const jumpKey = (iref: ItemRef, dir: -1 | 1) => {
    const info = itemInfo(projectRef.current, iref);
    if (!info) return;
    const now = tRef.current - info.start;
    const times = keyTimes(info.keys);
    const target = dir < 0 ? [...times].reverse().find((x) => x < now - 20) : times.find((x) => x > now + 20);
    if (target !== undefined) seek(info.start + target);
  };
  // the kit's set, toggle and jump read the playhead and the project from refs, and run only in the panels' event
  // handlers; its value, state and count read the rendered project
  /* eslint-disable react-hooks/refs */
  const kitOf = (iref: ItemRef): KeyKit => {
    const info = itemInfo(project, iref);
    const local = info ? localIn(info, tMs) : 0;
    return {
      value: (prop) => valueAt(project, iref, prop, tMs),
      state: (prop) => (!info || !info.keys[prop]?.length ? 'none' : keyIndexAt(info.keys, prop, local) >= 0 ? 'on' : 'off'),
      set: (prop, v, key) => setValues(iref, { [prop]: v }, key ? `${key}:${iref.id}` : ''),
      toggle: (prop) => toggleKey(iref, prop),
      jump: (dir) => jumpKey(iref, dir),
      count: info ? keyTimes(info.keys).length : 0,
    };
  };
  /* eslint-enable react-hooks/refs */
  const selRef: ItemRef | null = sel.kind === 'segment' || sel.kind === 'text' || sel.kind === 'overlay' ? { kind: sel.kind, id: sel.id } : null;

  // ── the monitor: a click picks what is under it; the box's body moves it, its corners size it, its knob turns it ──
  const refOfBox = (b: Box): ItemRef => ({ kind: b.kind === 'piece' ? 'segment' : b.kind, id: b.id });
  const monPoint = (e: React.PointerEvent) => {
    const r = monitorRef.current?.getBoundingClientRect();
    if (!r || !r.width || !r.height) return { x: 0, y: 0 };
    return { x: ((e.clientX - r.left) * mon.w) / r.width, y: ((e.clientY - r.top) * mon.h) / r.height };
  };
  const startDrag = (e: React.PointerEvent, mode: 'move' | 'scale' | 'rotate', box: Box) => {
    if (!canEdit) return;
    const iref = refOfBox(box);
    const pt = monPoint(e);
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const p = projectRef.current;
    const now = tRef.current;
    const sizeProp: KeyProp = iref.kind === 'text' ? 'size' : iref.kind === 'overlay' ? 'width' : 'scale';
    dragRef.current = {
      mode,
      iref,
      sizeProp,
      x0: pt.x,
      y0: pt.y,
      cx,
      cy,
      v: { x: valueAt(p, iref, 'x', now), y: valueAt(p, iref, 'y', now), s: valueAt(p, iref, sizeProp, now), r: valueAt(p, iref, 'rotation', now) },
      d0: Math.max(1, Math.hypot(pt.x - cx, pt.y - cy)),
      a0: Math.atan2(pt.y - cy, pt.x - cx),
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  /** What is under the pointer: picked (a piece only moves once it is the selection), and dragged. */
  const onPick = (e: React.PointerEvent) => {
    const pt = monPoint(e);
    const box = engineRef.current?.boxAt(pt.x, pt.y) ?? null;
    if (!box) return;
    const iref = refOfBox(box);
    const already = !!selRef && selRef.kind === iref.kind && selRef.id === iref.id;
    if (!already) setSel(iref.kind === 'segment' ? { kind: 'segment', id: iref.id } : { kind: iref.kind, id: iref.id });
    if (box.kind !== 'piece' || already) startDrag(e, 'move', box);
  };
  const onDragMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const pt = monPoint(e);
    if (!d.moved && Math.hypot(pt.x - d.x0, pt.y - d.y0) < 3) return;
    d.moved = true;
    const key = `drag:${d.iref.id}`;
    if (d.mode === 'move') {
      let x = d.v.x + (pt.x - d.x0) / mon.w;
      let y = d.v.y + (pt.y - d.y0) / mon.h;
      // the centre snaps to the frame's centre lines
      const centre = d.iref.kind === 'segment' ? 0 : 0.5;
      const snapX = Math.abs(x - centre) < 0.012;
      const snapY = Math.abs(y - centre) < 0.012;
      if (snapX) x = centre;
      if (snapY) y = centre;
      if (snapX !== guides.x || snapY !== guides.y) setGuides({ x: snapX, y: snapY });
      setValues(d.iref, { x, y }, key);
    } else if (d.mode === 'scale') {
      setValues(d.iref, { [d.sizeProp]: d.v.s * (Math.hypot(pt.x - d.cx, pt.y - d.cy) / d.d0) }, key);
    } else {
      let a = d.v.r + ((Math.atan2(pt.y - d.cy, pt.x - d.cx) - d.a0) * 180) / Math.PI;
      a = ((((a + 180) % 360) + 360) % 360) - 180;
      // steps of 15° with Shift; otherwise a right angle catches within 4°
      const step = e.shiftKey ? 15 : 90;
      const near = Math.round(a / step) * step;
      if (e.shiftKey || Math.abs(a - near) < 4) a = near;
      setValues(d.iref, { rotation: Math.round(a * 10) / 10 }, key);
    }
  };
  const onScaleDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (selBox) startDrag(e, 'scale', selBox);
  };
  const onTurnDown = (e: React.PointerEvent) => {
    e.stopPropagation();
    if (selBox) startDrag(e, 'rotate', selBox);
  };
  const endDrag = () => {
    dragRef.current = null;
    if (guides.x || guides.y) setGuides({ x: false, y: false });
  };
  const fullscreen = () => {
    const el = monitorRef.current;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el?.requestFullscreen?.();
  };

  // ── the tracks: hidden (not drawn, not exported), locked (nothing on them moves) ──
  const toggleLane = (what: 'hidden' | 'locked', lane: Lane) =>
    commit((p) => {
      const list = p.lanes[what];
      return { ...p, lanes: { ...p.lanes, [what]: list.includes(lane) ? list.filter((l) => l !== lane) : [...list, lane] } };
    });
  /** The timeline taller or shorter by its splitter. */
  const onSplitDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const y0 = e.clientY;
    const h0 = tlH;
    const max = Math.max(200, (rootRef.current?.clientHeight ?? 800) - 260);
    const move = (ev: PointerEvent) => setTlH(Math.round(clamp(h0 - (ev.clientY - y0), 170, max)));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // ── an item's menu: cut, duplicate, delete, copy and paste its style, its speed, a frozen frame ──
  const removeSel = (x: Selection) => {
    if (x.kind === 'segment') removePiece(x.id);
    else if (x.kind === 'audio') removeAudio(x.id);
    else if (x.kind === 'strap') removeStrap(x.id);
    else if (x.kind === 'dip') removeDip(x.id);
    else if (x.kind === 'text') removeText(x.id);
    else if (x.kind === 'overlay') removeOverlay(x.id);
  };
  /** The item cut in two at the playhead (when it is under it). */
  const cutItem = (x: Selection) => {
    if (!canEdit) return;
    const at = tRef.current;
    if (x.kind === 'segment') {
      const hit = segmentAt(projectRef.current, at);
      if (hit?.segment.id === x.id) commit((q) => splitAt(q, at));
      else setNotice(t('veNothingToCut'));
    } else if (x.kind === 'text') commit((q) => splitTextAt(q, x.id, at));
    else if (x.kind === 'audio') commit((q) => splitAudioAt(q, x.id, at));
  };
  /** A copy of the item right after it. */
  const duplicate = (x: Selection) => {
    if (!canEdit) return;
    const p = projectRef.current;
    if (x.kind === 'segment') {
      const i = p.segments.findIndex((it) => it.id === x.id);
      const it = p.segments[i];
      if (!it) return;
      const copy = { ...it, id: segmentId(), transitionIn: null };
      commit((q) => ({ ...q, segments: [...q.segments.slice(0, i + 1), copy, ...q.segments.slice(i + 1)] }));
      setSel({ kind: 'segment', id: copy.id });
    } else if (x.kind === 'text') {
      const it = p.texts.find((v) => v.id === x.id);
      if (!it) return;
      const copy = { ...it, id: textId(), atMs: it.atMs + it.durationMs };
      commit((q) => ({ ...q, texts: [...q.texts, copy] }));
      setSel({ kind: 'text', id: copy.id });
    } else if (x.kind === 'overlay') {
      const it = p.overlays.find((v) => v.id === x.id);
      if (!it) return;
      const copy = { ...it, id: overlayId(), atMs: it.atMs + it.durationMs };
      commit((q) => ({ ...q, overlays: [...q.overlays, copy] }));
      setSel({ kind: 'overlay', id: copy.id });
    } else if (x.kind === 'audio') {
      const it = p.audio.find((v) => v.id === x.id);
      if (!it) return;
      const copy = { ...it, id: audioClipId(), atMs: audioEndMs(it) };
      commit((q) => ({ ...q, audio: [...q.audio, copy] }));
      setSel({ kind: 'audio', id: copy.id });
    } else if (x.kind === 'strap') {
      const it = p.straps.find((v) => v.id === x.id);
      if (!it) return;
      const copy = { ...it, id: strapId(), atMs: it.atMs + it.durationMs };
      commit((q) => ({ ...q, straps: [...q.straps, copy] }));
      setSel({ kind: 'strap', id: copy.id });
    }
  };
  const copyStyle = (x: Selection) => {
    const p = projectRef.current;
    if (x.kind === 'segment') {
      const it = p.segments.find((v) => v.id === x.id);
      if (it) setStyleClip({ kind: 'segment', look: it.look, transform: it.transform, motion: it.motion, effects: it.effects });
    } else if (x.kind === 'text') {
      const it = p.texts.find((v) => v.id === x.id);
      if (it) setStyleClip({ kind: 'text', style: it.style, animIn: it.animIn, animOut: it.animOut, animMs: it.animMs });
    } else if (x.kind === 'overlay') {
      const it = p.overlays.find((v) => v.id === x.id);
      if (it) setStyleClip({ kind: 'overlay', width: it.width, opacity: it.opacity, rotation: it.rotation, animIn: it.animIn, animOut: it.animOut, animMs: it.animMs });
    } else return;
    setNotice(t('veStyleCopied'));
  };
  const pasteStyle = (x: Selection) => {
    const c = styleClip;
    if (!canEdit || !c || c.kind !== x.kind) return;
    if (c.kind === 'segment') commit((q) => ({ ...q, segments: q.segments.map((v) => (v.id === (x as { id: string }).id ? { ...v, look: c.look, transform: c.transform, motion: c.motion, effects: c.effects } : v)) }));
    else if (c.kind === 'text') commit((q) => ({ ...q, texts: q.texts.map((v) => (v.id === (x as { id: string }).id ? { ...v, style: { ...c.style, x: v.style.x, y: v.style.y }, animIn: c.animIn, animOut: c.animOut, animMs: c.animMs } : v)) }));
    else commit((q) => ({ ...q, overlays: q.overlays.map((v) => (v.id === (x as { id: string }).id ? { ...v, width: c.width, opacity: c.opacity, rotation: c.rotation, animIn: c.animIn, animOut: c.animOut, animMs: c.animMs } : v)) }));
  };
  const menuItems = (x: Selection): Array<{ icon: string; label: string; kbd?: string; go: () => void; off?: boolean } | '-'> => {
    const items: Array<{ icon: string; label: string; kbd?: string; go: () => void; off?: boolean } | '-'> = [];
    if (x.kind === 'segment' || x.kind === 'text' || x.kind === 'audio') items.push({ icon: '✂', label: t('veCutHere'), kbd: 'C', go: () => cutItem(x) });
    if (x.kind !== 'dip') items.push({ icon: '⧉', label: t('veDuplicate'), kbd: 'Ctrl+D', go: () => duplicate(x) });
    if (x.kind === 'segment' || x.kind === 'text' || x.kind === 'overlay') {
      items.push('-', { icon: '⎘', label: t('veCopyStyle'), kbd: 'Ctrl+C', go: () => copyStyle(x) }, { icon: '⎗', label: t('vePasteStyle'), kbd: 'Ctrl+V', go: () => pasteStyle(x), off: !styleClip || styleClip.kind !== x.kind });
      items.push({ icon: '◆', label: t('veKeyAll'), kbd: 'K', go: () => toggleAllKeys({ kind: x.kind, id: x.id }) });
    }
    if (x.kind === 'segment') {
      const it = project.segments.find((v) => v.id === x.id);
      if (it && !isStill(it)) {
        items.push('-');
        for (const v of [0.5, 1, 2]) items.push({ icon: '»', label: `${t('veSpeed')} ${v}×${it.speed === v ? ' ✓' : ''}`, go: () => patchPiece(x.id, { speed: v }) });
        items.push({ icon: '❄', label: t('veFreeze'), kbd: 'F', go: freeze });
        items.push({ icon: it.audioLink ? '🔗' : '✂♪', label: it.audioLink ? t('veSoundRelink') : t('veSoundDetach'), kbd: 'L', go: () => toggleSoundApart(x), off: !it.audioLink && it.speed !== 1 });
      }
    }
    if (x.kind === 'audio' && project.audio.find((v) => v.id === x.id)?.link) items.push({ icon: '🔗', label: t('veSoundRelink'), kbd: 'L', go: () => toggleSoundApart(x) });
    items.push('-', { icon: '🗑', label: t('veRemovePiece'), kbd: 'Del', go: () => removeSel(x) });
    return items;
  };

  // ── what is on screen ──
  const sourceTitle = edit.media?.title ?? edit.mediaAssetId;
  const strapSel = sel.kind === 'strap' ? project.straps.find((s) => s.id === sel.id) : undefined;
  const dipSel = sel.kind === 'dip' ? project.dips.find((d) => d.id === sel.id) : undefined;
  const canExport = !!codecs || !!renderMime();
  const exportTitle = codecs ? t('veExportFastHint') : renderMime() ? t('veExportSlowHint') : t('veNoRecorder');
  const selRow = rowOfSelection(sel, project);
  const analysing = sources.some((s) => !analyses[s.key]);
  const starts = segmentStarts(project);
  const libraryShown = library.filter((c) => !binQ.trim() || `${c.title} ${c.filename}`.toLowerCase().includes(binQ.trim().toLowerCase()));
  const isSequence = !isPlainCut(project, edit.mediaAssetId);
  const textSel = sel.kind === 'text' ? project.texts.find((x) => x.id === sel.id) : undefined;
  const overlaySel = sel.kind === 'overlay' ? project.overlays.find((o) => o.id === sel.id) : undefined;
  const mon = frameSize(project.format, 720);
  const selBox = sel.kind === 'text' || sel.kind === 'overlay' ? boxes.find((b) => b.kind === sel.kind && b.id === sel.id) : sel.kind === 'segment' ? boxes.find((b) => b.kind === 'piece' && b.id === sel.id) : undefined;
  const fitScale = Math.max(0.05, Math.min((stageSize.w - 16) / mon.w, (stageSize.h - 16) / mon.h));
  const monScale = monZoom === 'fit' ? fitScale : fitScale * monZoom;
  const captions = project.texts.filter((x) => x.caption).length;
  /** A small picture of the selected piece, for the filter swatches. */
  const pieceThumb = (s: EditSegment | undefined): string | null => (!s ? null : s.source.kind === 'clip' ? `/api/media/${encodeURIComponent(s.source.id)}/thumb` : s.source.kind === 'image' ? `/api/uploads/${encodeURIComponent(s.source.id)}/file` : (analyses[sourceKey(s.source)]?.thumbs[0]?.url ?? null));
  const thumbOfSel = pieceThumb(selectedSegment ?? segmentAt(project, tMs)?.segment);
  const firstId = selectedSegment?.id ?? project.segments[0]?.id ?? null;
  const hasMusic = project.audio.some((c) => c.track === 'music');
  const voClips = project.audio.filter((c) => c.track === 'vo');
  const musicClips = project.audio.filter((c) => c.track === 'music');
  const soundClips = project.audio.filter((c) => c.track === 'sound');
  /** The selection's separated sound, if it has one: the piece's link, or its clip's on A3. */
  const soundLinkSel = selectedSegment?.audioLink ?? selectedAudio?.link ?? null;
  const segIndex = selectedSegment ? project.segments.indexOf(selectedSegment) : -1;

  /** An audio clip's waveform: a take's or a bed's own; a separated sound's, its file's from the pieces' analysis. */
  const waveOf = (c: EditAudioClip): Wave | null => {
    const key = audioSourceKey(c.source);
    if (!isPieceSound(c.source)) return waves[key] ?? null;
    const a = analyses[key];
    return a?.wave && a.durationS > 0 ? { durationMs: Math.round(a.durationS * 1000), wave: a.wave } : null;
  };
  const rows = useMemo(() => rowsOf(project, { canEdit }, sel), [project, canEdit, sel]);
  const isSel = (a: TlAction) => !!a.selected;
  const renderAction = (action: TlAction) => {
    const g = segmentIdOf(action.id);
    if (g) {
      const seg = project.segments.find((x) => x.id === g);
      if (!seg) return null;
      return <PieceChip action={action} segment={seg} analysis={analyses[sourceKey(seg.source)] ?? null} pxPerS={pxPerS} title={sourceInfo(seg.source).title} selected={isSel(action)} />;
    }
    const sid = strapIdOf(action.id);
    if (sid) {
      const s = project.straps.find((x) => x.id === sid);
      return (
        <div className={`ve-act strap${isSel(action) ? ' sel' : ''}`} title={s?.lines.join(' · ')}>
          <span className="ve-act-icon">T</span>
          <span className="ve-act-text">{s?.lines[0]?.trim() || t('veStrapEmpty')}</span>
        </div>
      );
    }
    const aid = audioIdOf(action.id);
    if (aid) {
      const c = project.audio.find((x) => x.id === aid);
      if (!c) return null;
      return <AudioChip clip={c} wave={waveOf(c)} pxPerS={pxPerS} title={audioInfo(c.source).title} selected={isSel(action)} />;
    }
    const xid = textIdOf(action.id);
    if (xid) {
      const x = project.texts.find((it) => it.id === xid);
      return (
        <div className={`ve-act text${x?.caption ? ' caption' : ''}${isSel(action) ? ' sel' : ''}`} title={x?.text}>
          <span className="ve-act-icon">{x?.caption ? 'CC' : 'Aa'}</span>
          <span className="ve-act-text">{x?.text.trim() || t('veStrapEmpty')}</span>
          {x && <KeyMarks keys={x.keys} pxPerS={pxPerS} />}
        </div>
      );
    }
    const oid = overlayIdOf(action.id);
    if (oid) {
      const o = project.overlays.find((it) => it.id === oid);
      const u = o ? uploads.find((it) => it.id === o.source.id) : undefined;
      return (
        <div className={`ve-act overlay${isSel(action) ? ' sel' : ''}`} title={u?.title}>
          {o && <img className="ve-act-pic" src={`/api/uploads/${encodeURIComponent(o.source.id)}/file`} alt="" draggable={false} />}
          <span className="ve-act-text">{u?.title ?? t('veTrackOverlay')}</span>
          {o && <KeyMarks keys={o.keys} pxPerS={pxPerS} />}
        </div>
      );
    }
    const did = dipIdOf(action.id);
    if (did) {
      const d = project.dips.find((x) => x.id === did);
      return (
        <div className={`ve-act dip${isSel(action) ? ' sel' : ''}`} title={t('veDip')}>
          <span className="ve-act-text">▽ {Math.round((d?.level ?? 0) * 100)}%</span>
        </div>
      );
    }
    return null;
  };
  const onTimelineChange = (data: TlRow[]) => {
    if (canEdit) commit((p) => projectFromRows(p, data));
  };
  const muteSolo = (k: RowKey) => (k === 'video' ? 'video' : k === 'vo' ? 'vo' : k === 'music' ? 'music' : k === 'sound' ? 'sound' : null);

  const railItems: Array<{ id: string; icon: string; label: string; on: boolean; go: () => void }> =
    sel.kind === 'segment' && selectedSegment
      ? PIECE_TABS.map((pt) => ({ id: pt.id, icon: PIECE_ICONS[pt.id], label: pt.label(), on: pieceTab === pt.id, go: () => setPieceTab(pt.id) }))
      : sel.kind === 'mix' || sel.kind === 'format'
        ? []
        : [{ id: 'sel', icon: sel.kind === 'text' || sel.kind === 'texts' ? 'Aa' : sel.kind === 'overlay' || sel.kind === 'overlays' ? '🖼' : sel.kind === 'strap' || sel.kind === 'straps' ? 'T' : sel.kind === 'dip' ? '▽' : '♪', label: sel.kind === 'text' || sel.kind === 'texts' ? t('veText') : sel.kind === 'overlay' || sel.kind === 'overlays' ? t('veTrackOverlay') : sel.kind === 'strap' || sel.kind === 'straps' ? t('veStraps') : t('veBinAudio'), on: true, go: () => undefined }];
  const inspTitle =
    sel.kind === 'segment' && selectedSegment
      ? `${selectedSegment.source.kind === 'image' ? '🖼' : selectedSegment.freeze ? '❄' : '🎬'} ${t('vePiece')} ${segIndex + 1}/${project.segments.length} · ${PIECE_TABS.find((pt) => pt.id === pieceTab)?.label() ?? ''}`
      : sel.kind === 'text'
        ? `Aa ${textSel?.caption ? t('veCaption') : t('veText')}`
        : sel.kind === 'overlay' || sel.kind === 'overlays'
          ? `🖼 ${t('veTrackOverlay')}`
          : sel.kind === 'texts'
            ? `Aa ${t('veTrackText')}`
            : sel.kind === 'strap' || sel.kind === 'straps'
              ? `T ${t('veStraps')}`
              : sel.kind === 'audio' && selectedAudio
                ? `${TRACK[selectedAudio.track].icon} ${audioInfo(selectedAudio.source).title}`
                : sel.kind === 'track'
                  ? `${TRACK[sel.track].icon} ${TRACK[sel.track].name()}`
                  : sel.kind === 'dip'
                    ? `▽ ${t('veDip')}`
                    : sel.kind === 'mix'
                      ? `🎚 ${t('veMixer')}`
                      : `▭ ${t('veBinFormat')}`;

  return (
    <StrapDesignContext.Provider value={designOf(project)}>
    <div className="ve ve-fit" ref={rootRef} tabIndex={0} onKeyDown={onKey} style={{ height: `calc(100vh - ${fitTop}px)` }}>
      <header className="ve-bar">
        <Link
          className="btn ve-back"
          to={`/story/${edit.storyId}/edit`}
          title={`${t('veBack')}: ${edit.storyTitle} · ${t('veToStoryHint')}`}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.button !== 0) return;
            e.preventDefault();
            if (!busy) void toStory();
          }}
        >
          <b>‹ {t('veToStory')}</b>
          <span className="ve-back-story">{busy === 'story' ? `${t('veToStoryBusy')} ${exporting !== null ? `${Math.round(exporting * 100)}%` : '…'}` : edit.storyTitle}</span>
        </Link>
        {canEdit && (
          <button className="btn ve-leave" type="button" disabled={!!busy} onClick={leaveUnsaved} title={busy ? t('veLeaveBusy') : dirty ? t('veLeaveHint') : t('veLeaveNothing')}>
            ✕ {t('veLeave')}
          </button>
        )}
        <div className="ve-title">
          <span className="ve-kicker">✂ {t('veTitle')}</span>
          <h1>{edit.title || sourceTitle}</h1>
        </div>
        <span className="ve-meta">
          {project.segments.length} {t('vePieces')} · {timecode(totalMs)}
        </span>
        <span className="ve-grow" />
        <select className="ve-aspect-pick" value={project.format.aspect} disabled={!canEdit} onChange={(e) => patchFormat({ aspect: e.target.value as EditFormat['aspect'] })} title={t('veBinFormat')}>
          {ASPECTS.map((a) => (
            <option key={a} value={a}>
              ▭ {a}
            </option>
          ))}
        </select>
        <div className="ve-status-wrap">
          {/* the saved video is of the edit as last saved: changes the page still holds are not in it */}
          <button type="button" className={`ve-status-chip ${edit.rendered && !dirty ? 'ok' : 'no'}`} onClick={() => setStatusOpen((x) => !x)} aria-expanded={statusOpen} title={t('veStatus')}>
            {edit.rendered && !dirty ? `✓ ${t('veStatusRendered')}` : `✗ ${t('veStatusNotRendered')}`} ▾
          </button>
          {statusOpen && (
            <div className="ve-status-pop">
              <h4>{t('veStatus')}</h4>
            <ul className="ve-checks">
              <li className="ok">
                ✓ {project.segments.length} {t('vePieces')} · {timecode(totalMs)}
                {project.segments.some((s) => s.transitionIn) ? ` · ${project.segments.filter((s) => s.transitionIn).length} ${t('veTransitions')}` : ''}
              </li>
              <li className={project.straps.length ? 'ok' : 'off'}>{project.straps.length ? `✓ ${project.straps.length} ${t('hubCheckStraps')}` : `— ${t('hubCheckNoStraps')}`}</li>
              {(project.texts.length > 0 || project.overlays.length > 0) && (
                <li className="ok">
                  ✓ {project.texts.length} {t('veTextsCount')}
                  {captions ? ` (${captions} CC)` : ''} · {project.overlays.length} {t('veOverlaysCount')}
                </li>
              )}
              <li className="ok">
                ✓ {project.format.aspect} · {frameSize(project.format, project.output.resolution).w}×{frameSize(project.format, project.output.resolution).h} · {project.output.fps} fps
              </li>
              <li className={voClips.length ? 'ok' : 'off'}>{voClips.length ? `✓ ${t('veVo')} · ${voClips.length}` : `— ${t('veNoVo')}`}</li>
              <li className={musicClips.length ? 'ok' : 'off'}>{musicClips.length ? `✓ ${t('veMusic')} · ${musicClips.length}${project.dips.length ? ` · ${project.dips.length} ${t('veDips').toLowerCase()}` : ''}` : `— ${t('veNoMusic')}`}</li>
              <li className={edit.rendered ? 'ok' : 'no'}>
                {edit.rendered ? `✓ ${t('veStatusRendered')} ${edit.rendered.mime === 'video/mp4' ? 'MP4' : 'WebM'} · ${new Date(edit.rendered.at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })} · ${sizeOf(edit.rendered.bytes)}` : `✗ ${t('veStatusNotRendered')}`}
              </li>
              <li className="off">— {isSequence ? t('veAirNoteSequence') : t('veAirNote')}</li>
            </ul>
            </div>
          )}
        </div>
        <span className={`ve-save ${saving}`}>{saving === 'saving' ? t('veSaving') : saving === 'conflict' ? t('veConflict') : dirty ? t('veUnsaved') : t('veSaved')}</span>
        <button className="btn ve-ai-btn" type="button" disabled={!canEdit || !!busy} onClick={() => setAiOpen(true)} title={t('veAiEditHint')}>
          ✦ {t('veAiEdit')}
        </button>
        {edit.rendered && (
          <a className="btn" href={`/api/edits/${edit.id}/output?v=${encodeURIComponent(edit.rendered.at)}`} download={`${(edit.title || sourceTitle).replace(/[^\p{L}\p{N}_-]+/gu, '_')}.${edit.rendered.mime === 'video/mp4' ? 'mp4' : 'webm'}`} title={t('veDownload')}>
            ⤓
          </a>
        )}
        <button className="btn" type="button" disabled={!canEdit || !!busy} onClick={apply} title={t('veApplyHint')}>
          {busy === 'apply' ? '…' : `▦ ${t('veApply')}`}
        </button>
        <button
          className={`btn ve-air-btn${air.active ? ' on' : air.view?.air?.status === 'FAILED' ? ' failed' : air.view?.air?.status === 'DONE' && air.view.air.linked ? ' done' : ''}`}
          type="button"
          disabled={!!busy}
          onClick={() => {
            void air.refresh();
            setAirOpen(true);
          }}
          title={t('veAirHint')}
        >
          📡 {air.active ? airLine(air.view?.air ?? null) : t('veAir')}
        </button>
        <button className="btn primary" type="button" disabled={!canEdit || !!busy || (!canExport && !dirty)} onClick={exportNow} title={exportTitle}>
          {busy === 'export' ? `${t('veExporting')} ${Math.round((exporting ?? 0) * 100)}%` : `💾 ${t('veSaveChanges')}`}
        </button>
      </header>
      {notice && (
        <div className="banner info ve-notice">
          {notice}
          <button type="button" className="ve-notice-x" onClick={() => setNotice(null)} aria-label="×">
            ×
          </button>
        </div>
      )}
      {draft && canEdit && (
        <div className="banner info ve-notice ve-draft">
          <span>
            {t('veDraftFound')} ({new Date(draft.at).toLocaleString('el-GR', { timeZone: 'Europe/Nicosia', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})
          </span>
          <button type="button" className="btn small primary" onClick={restoreDraft}>
            {t('veDraftRestore')}
          </button>
          <button type="button" className="btn small" onClick={discardDraft}>
            {t('veDraftDiscard')}
          </button>
        </div>
      )}

      <div className="ve-work">
        <nav className="ve-lrail" role="tablist" aria-label={t('veTools')}>
          {BIN_TABS.map((b) => (
            <button key={b.id} type="button" role="tab" aria-selected={binTab === b.id} className={`ve-rail-item${binTab === b.id ? ' sel' : ''}`} onClick={() => setBinTab(b.id)} title={b.label()}>
              <span className="ve-rail-icon">{b.icon}</span>
              <span className="ve-rail-label">{b.label()}</span>
              {b.id === 'library' && <b className="ve-rail-count">{library.length}</b>}
              {b.id === 'uploads' && videoUploads.length > 0 && <b className="ve-rail-count">{videoUploads.length}</b>}
              {b.id === 'images' && imageUploads.length > 0 && <b className="ve-rail-count">{imageUploads.length}</b>}
            </button>
          ))}
        </nav>
        <aside className="ve-side ve-binpanel">
          <div className="ve-side-head">
            <b>{BIN_TABS.find((b) => b.id === binTab)?.label()}</b>
          </div>
          <div className="ve-side-body">
          {binTab === 'library' && (
            <>
              <div className="ve-bin-head">
                <input type="search" value={binQ} placeholder={t('veBinSearch')} onChange={(e) => setBinQ(e.target.value)} />
              </div>
              <p className="ve-note">{t('veBinDrag')}</p>
              <div className="ve-bin-list">
                {libraryShown.length === 0 && <p className="ve-note">{t('veBinEmpty')}</p>}
                {libraryShown.map((c) => (
                  <div key={c.id} className="ve-bin-item" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'clip', id: c.id, durationMs: c.durationMs })} onDragEnd={dragEnd} title={c.filename}>
                    <img className="ve-bin-thumb" alt="" loading="lazy" src={`/api/media/${encodeURIComponent(c.id)}/thumb`} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />
                    <div className="ve-bin-meta">
                      <b>{c.title}</b>
                      <span>
                        {mmss(Math.round(c.durationMs / 1000))} · {c.status}
                      </span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => addSource({ kind: 'clip', id: c.id }, c.durationMs)} title={t('veBinAdd')}>
                      ＋
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {binTab === 'uploads' && (
            <>
              <label className="btn primary small" title={t('veImportHint')}>
                ⤒ {busy === 'import' ? t('veImporting') : t('veImport')}
                <input type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" hidden disabled={!canEdit || !!busy} onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} />
              </label>
              <p className="ve-note">{t('veImportHint')}</p>
              <div className="ve-bin-list">
                {videoUploads.length === 0 && <p className="ve-note">{t('veNoUploads')}</p>}
                {videoUploads.map((u) => (
                  <div key={u.id} className="ve-bin-item" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'upload', id: u.id, durationMs: u.durationMs ?? 5000 })} onDragEnd={dragEnd} title={u.filename}>
                    <div className="ve-bin-thumb blank">⤒</div>
                    <div className="ve-bin-meta">
                      <b>{u.title}</b>
                      <span>
                        {u.durationMs ? `${mmss(Math.round(u.durationMs / 1000))} · ` : ''}
                        {sizeOf(u.bytes)}
                      </span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => addSource({ kind: 'upload', id: u.id }, u.durationMs)} title={t('veBinAdd')}>
                      ＋
                    </button>
                    <button type="button" className="btn icon danger" disabled={!canEdit || !!busy} onClick={() => removeUpload(u.id)} title={t('veRemoveUpload')}>
                      ✖
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {binTab === 'audio' && (
            <>
              <p className="ve-note">{t('veAudioBinHint')}</p>
              <h5 className="ve-bin-h">🎙 {t('veTrackVo')}</h5>
              <div className="ve-bin-list">
                {voiceovers.length === 0 && <p className="ve-note">{t('voNone')}</p>}
                {voiceovers.map((v) => (
                  <div key={v.id} className="ve-bin-item audio" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'take', id: v.id, durationMs: v.durationMs })} onDragEnd={dragEnd} title={v.text}>
                    <div className="ve-bin-thumb blank vo">🎙</div>
                    <div className="ve-bin-meta">
                      <b>{v.kind === 'ai' ? `✦ ${v.voice ?? t('voAi')}` : (v.createdBy ?? t('voRecorded'))}</b>
                      <span>
                        {v.durationMs ? `${s1(v.durationMs)}″ · ` : ''}
                        {v.text.slice(0, 40)}
                      </span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => void addAudio({ kind: 'voiceover', id: v.id }, 'vo', v.durationMs)} title={t('veAddAtPlayhead')}>
                      ＋
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn small" onClick={() => setSel({ kind: 'track', track: 'vo' })}>
                ● {t('veNewTake')}
              </button>
              <h5 className="ve-bin-h">♪ {t('veTrackMusic')}</h5>
              <div className="ve-bin-list">
                {music.length === 0 && <p className="ve-note">{t('veNoBeds')}</p>}
                {music.map((m) => (
                  <div key={m.id} className="ve-bin-item audio" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'bed', id: m.id, durationMs: m.durationMs })} onDragEnd={dragEnd} title={m.title}>
                    <div className="ve-bin-thumb blank music">♪</div>
                    <div className="ve-bin-meta">
                      <b>{m.title}</b>
                      <span>{m.durationMs ? mmss(Math.round(m.durationMs / 1000)) : ''}</span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => void addAudio({ kind: 'music', id: m.id }, 'music', m.durationMs)} title={t('veAddAtPlayhead')}>
                      ＋
                    </button>
                  </div>
                ))}
              </div>
              <label className="btn small">
                ⤒ {busy === 'music' ? '…' : t('veUploadMusic')}
                <input type="file" accept="audio/*" hidden disabled={!canEdit || !!busy} onChange={(e) => e.target.files?.[0] && uploadMusic(e.target.files[0])} />
              </label>
            </>
          )}
          {binTab === 'transitions' && (
            <>
              <p className="ve-note">{t('veTrHint')}</p>
              <div className="ve-bin-list">
                {TRANSITION_TYPES.map((type) => (
                  <div key={type} className="ve-bin-item ve-tr-card" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'transition', type })} onDragEnd={dragEnd}>
                    <div className={`ve-tr-icon ${type}`} />
                    <div className="ve-bin-meta">
                      <b>{TRANSITION_WORDS[type]()}</b>
                      <span>{t('veTrDefault')}</span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => putTransition(type)} title={t('veTrAddHint')}>
                      ＋
                    </button>
                  </div>
                ))}
                <div className="ve-bin-item ve-tr-card" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'endfade', color: 'black' })} onDragEnd={dragEnd}>
                  <div className="ve-tr-icon end-fade" />
                  <div className="ve-bin-meta">
                    <b>{t('veEndFade')}</b>
                    <span>{t('veEndFadeHint')}</span>
                  </div>
                  <button type="button" className="btn icon" disabled={!canEdit} onClick={() => commit((p) => ({ ...p, endFade: p.endFade ? null : { color: 'black', durationMs: 1000 } }))} title={t('veEndFade')}>
                    {project.endFade ? '✓' : '＋'}
                  </button>
                </div>
              </div>
            </>
          )}
          {binTab === 'lt' && (
            <>
              <p className="ve-note">{t('veLtHint')}</p>
              <label className="ve-lt-design">
                <span>{t('veLtDesign')}</span>
                <select value={project.strapDesign ?? ''} disabled={!canEdit} onChange={(e) => commit((p) => ({ ...p, strapDesign: (e.target.value || null) as StrapDesignKey | null }))} title={t('veLtDesignHint')}>
                  <option value="">
                    {t('veLtDesignBulletin')} ({strapDesign(bulletinDesign).name})
                  </option>
                  {STRAP_DESIGNS.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className="ve-bin-list">
                {LOWER_THIRDS.map((lt) => (
                  <div key={lt.template} className="ve-lt-card" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'strap', template: lt.template })} onDragEnd={dragEnd}>
                    <StrapArtView lines={lt.lines()} />
                    <div className="ve-lt-row">
                      <b>{lt.name()}</b>
                      <button type="button" className="btn small" disabled={!canEdit} onClick={() => addStrap([''], tRef.current, lt.template)} title={t('veLtAdd')}>
                        ＋ {t('veLtAdd')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {binTab === 'images' && (
            <>
              <label className="btn primary small" title={t('veImagesHint')}>
                ⤒ {busy === 'import' ? t('veImporting') : t('veImportPhoto')}
                <input type="file" accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp" hidden disabled={!canEdit || !!busy} onChange={(e) => e.target.files?.[0] && importImage(e.target.files[0])} />
              </label>
              <p className="ve-note">{t('veImagesHint')}</p>
              <div className="ve-bin-list">
                {imageUploads.length === 0 && <p className="ve-note">{t('veNoImages')}</p>}
                {imageUploads.map((u) => (
                  <div key={u.id} className="ve-bin-item" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'image', id: u.id })} onDragEnd={dragEnd} title={u.filename}>
                    <img className="ve-bin-thumb photo" alt="" loading="lazy" src={`/api/uploads/${encodeURIComponent(u.id)}/file`} />
                    <div className="ve-bin-meta">
                      <b>{u.title}</b>
                      <span>{sizeOf(u.bytes)}</span>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => addPhoto(u.id, tRef.current)} title={t('veAddPhotoPiece')}>
                      V1
                    </button>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => addOverlay(u.id)} title={t('veAddOverlay')}>
                      V2
                    </button>
                    <button type="button" className="btn icon danger" disabled={!canEdit || !!busy} onClick={() => removeUpload(u.id)} title={t('veRemoveUpload')}>
                      ✖
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {binTab === 'text' && (
            <>
              <p className="ve-note">{t('veTextBinHint')}</p>
              <h5 className="ve-bin-h">{t('veTemplates')}</h5>
              <div className="ve-tpl-grid">
                {TEXT_TEMPLATES.map((pr) => (
                  <button key={pr.id} type="button" className="ve-tpl-card" draggable={canEdit} disabled={!canEdit} onDragStart={(e) => dragStart(e, { kind: 'text', preset: pr.id })} onDragEnd={dragEnd} onClick={() => addText(pr.id)} title={t('veTextAddHint')}>
                    <TemplateThumb preset={pr} />
                    <span className="ve-text-name">{t(pr.name)}</span>
                  </button>
                ))}
              </div>
              <h5 className="ve-bin-h">{t('vePlainTexts')}</h5>
              <div className="ve-text-grid">
                {TEXT_PRESETS.map((pr) => (
                  <button key={pr.id} type="button" className="ve-text-card" draggable={canEdit} disabled={!canEdit} onDragStart={(e) => dragStart(e, { kind: 'text', preset: pr.id })} onDragEnd={dragEnd} onClick={() => addText(pr.id)} title={t('veTextAddHint')}>
                    <span className="ve-text-sample" style={presetCss(pr.style)}>
                      {t(pr.sample)}
                    </span>
                    <span className="ve-text-name">{t(pr.name)}</span>
                  </button>
                ))}
              </div>
              <h5 className="ve-bin-h">CC {t('veCaptions')}</h5>
              <p className="ve-note">{t('veAutoCaptionsHint')}</p>
              <div className="ve-field">
                <button type="button" className="btn small primary" disabled={!canEdit || !voClips.length} onClick={autoCaptions}>
                  ✦ {t('veAutoCaptions')}
                </button>
                {captions > 0 && (
                  <button type="button" className="btn small" disabled={!canEdit} onClick={clearCaptions}>
                    ✖ {t('veClearCaptions')} ({captions})
                  </button>
                )}
              </div>
            </>
          )}
          {binTab === 'filters' && (
            <>
              <p className="ve-note">{t('veFiltersHint')}</p>
              <div className="ve-filter-grid bin">
                {FILTERS.map((f) => {
                  const css = lookPaint({ ...defaultLook(), filter: f }).css;
                  return (
                    <button key={f} type="button" className={`ve-filter${selectedSegment?.look.filter === f ? ' sel' : ''}`} draggable={canEdit} disabled={!canEdit} onDragStart={(e) => dragStart(e, { kind: 'filter', filter: f })} onDragEnd={dragEnd} onClick={() => applyFilter(f)} title={t(FILTER_WORDS[f])}>
                      <span className="ve-filter-swatch" style={{ backgroundImage: thumbOfSel ? `url(${thumbOfSel})` : undefined, filter: css === 'none' ? undefined : css }} />
                      <span className="ve-filter-name">{t(FILTER_WORDS[f])}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {binTab === 'effects' && (
            <>
              <p className="ve-note">{t('veEffectsHint')}</p>
              <div className="ve-bin-list">
                {EFFECT_TYPES.map((type) => (
                  <div key={type} className="ve-bin-item ve-fx-card" draggable={canEdit} onDragStart={(e) => dragStart(e, { kind: 'effect', type })} onDragEnd={dragEnd}>
                    <div className={`ve-fx-icon ${type}`}>{EFFECT_WORDS[type].icon}</div>
                    <div className="ve-bin-meta">
                      <b>{t(EFFECT_WORDS[type].key)}</b>
                    </div>
                    <button type="button" className="btn icon" disabled={!canEdit} onClick={() => addEffect(type)} title={t('veEffectAddHint')}>
                      ＋
                    </button>
                  </div>
                ))}
              </div>
              <h5 className="ve-bin-h">{t('veMotion')}</h5>
              <div className="ve-chip-row">
                {MOTIONS.filter((m) => m !== 'none').map((m) => (
                  <button key={m} type="button" className="ve-chip" draggable={canEdit} disabled={!canEdit} onDragStart={(e) => dragStart(e, { kind: 'motion', type: m })} onDragEnd={dragEnd} onClick={() => setMotion(m)} title={t('veEffectAddHint')}>
                    {MOTION_WORDS[m].icon} {t(MOTION_WORDS[m].key)}
                  </button>
                ))}
              </div>
              <h5 className="ve-bin-h">❄ {t('veFreeze')}</h5>
              <p className="ve-note">{t('veFreezeHint')}</p>
              <button type="button" className="btn small" disabled={!canEdit} onClick={freeze}>
                ❄ {t('veFreeze')} (F)
              </button>
            </>
          )}
          {binTab === 'format' && <FormatPanel format={project.format} output={project.output} canEdit={canEdit} onFormat={patchFormat} onOutput={patchOutput} />}
          </div>
        </aside>

        <section className="ve-preview">
          <div className="ve-stage" ref={stageRef}>
            <div
              ref={monitorRef}
              className={`ve-monitor${waiting ? ' waiting' : ''}`}
              style={{ width: Math.round(mon.w * monScale), height: Math.round(mon.h * monScale) }}
              onDragOver={(e) => {
                if (canEdit && e.dataTransfer.types.includes(DND)) e.preventDefault();
              }}
              onDrop={onMonitorDrop}
              onPointerMove={onDragMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onDoubleClick={() => engineRef.current?.toggle()}
            >
              <canvas ref={canvasRef} className="ve-canvas" onPointerDown={onPick} />
              {safe && (
                <div className="ve-safe" aria-hidden="true">
                  <span className="action" />
                  <span className="title" />
                </div>
              )}
              {grid && <div className="ve-grid" aria-hidden="true" />}
              {guides.x && <div className="ve-guide v" aria-hidden="true" />}
              {guides.y && <div className="ve-guide h" aria-hidden="true" />}
              {selBox && (
                <div
                  className={`ve-sel ${selBox.kind}${canEdit ? '' : ' ro'}`}
                  style={{ left: `${((selBox.x + selBox.w / 2) / mon.w) * 100}%`, top: `${((selBox.y + selBox.h / 2) / mon.h) * 100}%`, width: `${(selBox.w / mon.w) * 100}%`, height: `${(selBox.h / mon.h) * 100}%`, transform: `translate(-50%, -50%) rotate(${selBox.rot}deg)` }}
                  onPointerDown={onPick}
                >
                  {canEdit &&
                    (['nw', 'ne', 'sw', 'se'] as const).map((c) => <span key={c} className={`ve-h ${c}`} title={t('veHandleSize')} onPointerDown={onScaleDown} />)}
                  {canEdit && <span className="ve-h rot" title={t('veHandleTurn')} onPointerDown={onTurnDown} />}
                </div>
              )}
              {waiting && <div className="ve-waiting">{t('veWaiting')}</div>}
              {exporting !== null && (
                <div className="ve-rendering">
                  <div className="ve-rendering-bar">
                    <span style={{ width: `${Math.round(exporting * 100)}%` }} />
                  </div>
                  <div>
                    {t('veExporting')} {Math.round(exporting * 100)}%
                  </div>
                  <div className="ve-rendering-note">{t('veExportKeep')}</div>
                </div>
              )}
            </div>
          </div>
          <div className="ve-pbar">
            <span className="ve-pbar-tc">
              <b>{timecode(tMs)}</b> / {timecode(totalMs)}
            </span>
            <span className="ve-pbar-mid">
              <button className="btn icon" type="button" onClick={() => seek(0)} title={t('veToIn')}>
                ⏮
              </button>
              <button className="btn icon" type="button" onClick={() => seek(tRef.current - FRAME_MS)} title={t('veFrameBack')}>
                ◂
              </button>
              <button className="btn icon play" type="button" onClick={() => engineRef.current?.toggle()} title={playing ? t('vePause') : t('vePlay')}>
                {playing ? '❚❚' : '▶'}
              </button>
              <button className="btn icon" type="button" onClick={() => seek(tRef.current + FRAME_MS)} title={t('veFrameFwd')}>
                ▸
              </button>
              <button className="btn icon" type="button" onClick={() => seek(editDurationMs(projectRef.current))} title={t('veToOut')}>
                ⏭
              </button>
              <span className="ve-sep" />
              <button className="btn small" type="button" disabled={!canEdit} onClick={setIn} title={t('veSetInHint')}>
                ⟦ IN
              </button>
              <button className="btn small" type="button" disabled={!canEdit} onClick={setOut} title={t('veSetOutHint')}>
                OUT ⟧
              </button>
            </span>
            <span className="ve-pbar-right">
              <span className="ve-meter" title={t('veMeter')}>
                <span>
                  <span ref={meterL} />
                </span>
                <span>
                  <span ref={meterR} />
                </span>
              </span>
              <button
                type="button"
                className={`btn icon${safe ? ' on' : ''}`}
                onClick={() => {
                  setSafe(!safe);
                  savePref('ve-safe', !safe);
                }}
                title={t('veSafeArea')}
              >
                ⊡
              </button>
              <button
                type="button"
                className={`btn icon${grid ? ' on' : ''}`}
                onClick={() => {
                  setGrid(!grid);
                  savePref('ve-grid', !grid);
                }}
                title={t('veGrid')}
              >
                ▦
              </button>
              <select className="ve-zoom-pick" value={String(monZoom)} onChange={(e) => setMonZoom(e.target.value === 'fit' ? 'fit' : Number(e.target.value))} title={t('veMonZoom')}>
                {MON_ZOOMS.map((z) => (
                  <option key={String(z)} value={String(z)}>
                    {z === 'fit' ? 'Fit' : `${Math.round(z * 100)}%`}
                  </option>
                ))}
              </select>
              <button type="button" className="btn icon" onClick={fullscreen} title={t('veFullscreen')}>
                ⛶
              </button>
            </span>
          </div>
        </section>

        <aside className="ve-side ve-propspanel">
          <div className="ve-side-head">
            <b>{inspTitle}</b>
          </div>
          <div className="ve-side-body">
          {sel.kind === 'segment' && selectedSegment && (
            <section className="ve-panel">
              {pieceTab === 'color' && <LookPanel look={selectedSegment.look} thumb={thumbOfSel} canEdit={canEdit} onChange={(patch, key) => patchPiece(selectedSegment.id, { look: { ...selectedSegment.look, ...patch } }, key ? `${key}:${selectedSegment.id}` : '')} />}
              {pieceTab === 'frame' && (
                <FramePanel
                  transform={selectedSegment.transform}
                  motion={selectedSegment.motion}
                  kit={kitOf({ kind: 'segment', id: selectedSegment.id })}
                  canEdit={canEdit}
                  onTransform={(patch, key) => patchPiece(selectedSegment.id, { transform: { ...selectedSegment.transform, ...patch } }, key ? `${key}:${selectedSegment.id}` : '')}
                  onMotion={(patch, key) => patchPiece(selectedSegment.id, { motion: { ...selectedSegment.motion, ...patch } }, key ? `${key}:${selectedSegment.id}` : '')}
                  onReset={() => patchPiece(selectedSegment.id, { transform: { ...selectedSegment.transform, x: 0, y: 0, scale: 1, rotation: 0, opacity: 1, flipH: false, flipV: false }, keys: {} })}
                />
              )}
              {pieceTab === 'speed' && (
                <SpeedPanel
                  speed={selectedSegment.speed}
                  still={isStill(selectedSegment)}
                  freeze={selectedSegment.freeze}
                  lengthMs={segmentLengthMs(selectedSegment)}
                  canEdit={canEdit}
                  onSpeed={(v, key) => patchPiece(selectedSegment.id, { speed: v }, key ? `${key}:${selectedSegment.id}` : '')}
                  onFreeze={freeze}
                />
              )}
              {pieceTab === 'effects' && <EffectsPanel effects={selectedSegment.effects} canEdit={canEdit} onChange={(effects, key) => patchPiece(selectedSegment.id, { effects }, key ? `${key}:${selectedSegment.id}` : '')} />}
              {pieceTab === 'basic' && (
              <>
              <div className="ve-field">
                <label>{t('vePieceSource')}</label>
                <span className="ve-src">{sourceInfo(selectedSegment.source).title}</span>
              </div>
              {isStill(selectedSegment) ? (
                <div className="ve-field">
                  <label>{selectedSegment.freeze ? t('veFreezeFrame') : t('veLength')}</label>
                  {selectedSegment.freeze && <span className="ve-tc-small">{timecode(selectedSegment.inMs)} ·</span>}
                  <input type="number" min={0.1} step={0.1} value={s1(segmentLengthMs(selectedSegment))} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { outMs: selectedSegment.inMs + Math.round(Number(e.target.value) * 1000) }, `hold:${selectedSegment.id}`)} />
                  <span className="ve-unit">″</span>
                </div>
              ) : (
              <>
              <div className="ve-field">
                <label>{t('vePieceIn')}</label>
                <input type="number" min={0} step={0.1} value={s1(selectedSegment.inMs)} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { inMs: Math.round(Number(e.target.value) * 1000) }, `in:${selectedSegment.id}`)} />
                <span className="ve-unit">″</span>
                <span className="ve-tc-small">{timecode(selectedSegment.inMs)}</span>
              </div>
              <div className="ve-field">
                <label>{t('vePieceOut')}</label>
                <input type="number" min={0} step={0.1} value={s1(selectedSegment.outMs)} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { outMs: Math.round(Number(e.target.value) * 1000) }, `out:${selectedSegment.id}`)} />
                <span className="ve-unit">″</span>
                <span className="ve-tc-small">{timecode(selectedSegment.outMs)}</span>
              </div>
              </>
              )}
              <div className="ve-field">
                <label>{t('veLength')}</label>
                <span className="ve-tc-small">
                  {timecode(segmentLengthMs(selectedSegment))} · {t('veStrapAt')} {timecode(starts[segIndex] ?? 0)}
                </span>
              </div>
              {!isStill(selectedSegment) &&
                (selectedSegment.audioLink ? (
                  <div className="ve-field">
                    <label>{t('vePieceSound')}</label>
                    <span className="ve-src">🔊 {t('veSoundOnA3')}</span>
                    <button
                      className="btn small"
                      type="button"
                      onClick={() => {
                        const c = project.audio.find((a) => a.link === selectedSegment.audioLink);
                        setSel(c ? { kind: 'audio', id: c.id } : { kind: 'track', track: 'sound' });
                      }}
                    >
                      A3
                    </button>
                    <button className="btn small" type="button" disabled={!canEdit} onClick={() => toggleSoundApart({ kind: 'segment', id: selectedSegment.id })} title={t('veSoundRelinkHint')}>
                      🔗 {t('veSoundRelink')}
                    </button>
                  </div>
                ) : (
                  <div className="ve-field">
                    <label>{t('vePieceSound')}</label>
                    <input type="range" min={-30} max={6} step={0.5} value={Math.max(-30, toDb(selectedSegment.gain))} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { gain: Number(e.target.value) <= -30 ? 0 : fromDb(Number(e.target.value)) }, `gain:${selectedSegment.id}`)} />
                    <span className="ve-unit">{dbLabel(selectedSegment.gain)}</span>
                    <button className="btn small" type="button" disabled={!canEdit || selectedSegment.speed !== 1} onClick={() => toggleSoundApart({ kind: 'segment', id: selectedSegment.id })} title={selectedSegment.speed !== 1 ? t('veSoundSpeed') : t('veSoundDetachHint')}>
                      ✂♪ {t('veSoundDetach')}
                    </button>
                  </div>
                ))}
              <h4>⧓ {segIndex === 0 ? t('veTrFirst') : t('veTrFromPrev')}</h4>
              <div className="ve-field">
                <select value={selectedSegment.transitionIn?.type ?? ''} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { transitionIn: e.target.value ? { type: e.target.value as TransitionType, durationMs: selectedSegment.transitionIn?.durationMs ?? DEFAULT_TRANSITION_MS } : null })}>
                  <option value="">{segIndex === 0 ? t('veTrNoneFirst') : t('veTrCut')}</option>
                  {(segIndex === 0 ? (['dip-black', 'dip-white'] as TransitionType[]) : TRANSITION_TYPES).map((type) => (
                    <option key={type} value={type}>
                      {segIndex === 0 ? (type === 'dip-white' ? t('veFadeFromWhite') : t('veFadeFromBlack')) : TRANSITION_WORDS[type]()}
                    </option>
                  ))}
                </select>
                {selectedSegment.transitionIn && (
                  <>
                    <input type="number" min={0.2} max={4} step={0.1} value={s1(selectedSegment.transitionIn.durationMs)} disabled={!canEdit} onChange={(e) => patchPiece(selectedSegment.id, { transitionIn: { ...selectedSegment.transitionIn!, durationMs: Math.round(Number(e.target.value) * 1000) } }, `tr:${selectedSegment.id}`)} />
                    <span className="ve-unit">″</span>
                  </>
                )}
              </div>
              <div className="ve-field">
                <button className="btn small" type="button" disabled={!canEdit || segIndex === 0} onClick={() => movePiece(selectedSegment.id, -1)}>
                  ◂ {t('veMoveEarlier')}
                </button>
                <button className="btn small" type="button" disabled={!canEdit || segIndex === project.segments.length - 1} onClick={() => movePiece(selectedSegment.id, 1)}>
                  {t('veMoveLater')} ▸
                </button>
                <span className="ve-grow" />
                <button className="btn small danger" type="button" disabled={!canEdit || project.segments.length <= 1} onClick={() => removePiece(selectedSegment.id)} title={t('veRemovePieceHint')}>
                  🗑 {t('veRemovePiece')}
                </button>
              </div>
              <p className="ve-note">{t('vePieceHint')}</p>
              </>
              )}
            </section>
          )}

          {sel.kind === 'texts' && (
            <section className="ve-panel">
              <h4>
                Aa {t('veTrackText')}
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => addText('subtitle')} title={t('veTextAddHint')}>
                  ＋ {t('veAddText')}
                </button>
              </h4>
              {project.texts.length === 0 && <p className="ve-note">{t('veNoTexts')}</p>}
              <ul className="ve-list">
                {project.texts.map((x) => (
                  <li key={x.id} onClick={() => setSel({ kind: 'text', id: x.id })}>
                    <span className="ve-tc-small">{timecode(x.atMs)}</span> {x.caption ? 'CC ' : ''}
                    {x.text.trim() || t('veStrapEmpty')}
                  </li>
                ))}
              </ul>
              <div className="ve-field">
                <button type="button" className="btn small primary" disabled={!canEdit || !voClips.length} onClick={autoCaptions} title={t('veAutoCaptionsHint')}>
                  ✦ {t('veAutoCaptions')}
                </button>
                {captions > 0 && (
                  <button type="button" className="btn small" disabled={!canEdit} onClick={clearCaptions}>
                    ✖ {t('veClearCaptions')} ({captions})
                  </button>
                )}
              </div>
              <p className="ve-note">{t('veTextBinHint')}</p>
            </section>
          )}

          {sel.kind === 'text' && textSel && (
            <section className="ve-panel">
              <h4>
                {textSel.caption ? 'CC' : 'Aa'} {textSel.caption ? t('veCaption') : t('veText')}
                <button className="btn small" type="button" onClick={() => setSel({ kind: 'texts' })}>
                  T2
                </button>
              </h4>
              <TextPanel text={textSel} kit={kitOf({ kind: 'text', id: textSel.id })} canEdit={canEdit} nowMs={tMs} onChange={(patch, key) => patchText(textSel.id, patch, key ?? '')} onRemove={() => removeText(textSel.id)} onStyleAll={textSel.caption ? () => styleAllCaptions(textSel) : null} />
            </section>
          )}

          {sel.kind === 'overlays' && (
            <section className="ve-panel">
              <h4>🖼 {t('veTrackOverlay')}</h4>
              {project.overlays.length === 0 && <p className="ve-note">{t('veNoOverlays')}</p>}
              <ul className="ve-list">
                {project.overlays.map((o) => (
                  <li key={o.id} onClick={() => setSel({ kind: 'overlay', id: o.id })}>
                    <span className="ve-tc-small">
                      {timecode(o.atMs)} – {timecode(o.atMs + o.durationMs)}
                    </span>{' '}
                    {uploads.find((u) => u.id === o.source.id)?.title ?? '—'}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn small" onClick={() => setBinTab('images')}>
                🖼 {t('veBinImages')}
              </button>
              <p className="ve-note">{t('veImagesHint')}</p>
            </section>
          )}

          {sel.kind === 'overlay' && overlaySel && (
            <section className="ve-panel">
              <h4>
                🖼 {uploads.find((u) => u.id === overlaySel.source.id)?.title ?? t('veTrackOverlay')}
                <button className="btn small" type="button" onClick={() => setSel({ kind: 'overlays' })}>
                  V2
                </button>
              </h4>
              <OverlayPanel overlay={overlaySel} src={`/api/uploads/${encodeURIComponent(overlaySel.source.id)}/file`} kit={kitOf({ kind: 'overlay', id: overlaySel.id })} canEdit={canEdit} nowMs={tMs} onChange={(patch, key) => patchOverlay(overlaySel.id, patch, key ?? '')} onRemove={() => removeOverlay(overlaySel.id)} />
            </section>
          )}

          {sel.kind === 'format' && (
            <section className="ve-panel">
              <h4>▭ {t('veBinFormat')}</h4>
              <FormatPanel format={project.format} output={project.output} canEdit={canEdit} onFormat={patchFormat} onOutput={patchOutput} />
            </section>
          )}

          {(sel.kind === 'straps' || sel.kind === 'strap') && (
            <section className="ve-panel">
              <h4>
                T {t('veStraps')}
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => addStrap([''])} title={t('veAddStrapHint')}>
                  ＋ {t('veAddStrap')}
                </button>
              </h4>
              {project.straps.length === 0 && <p className="ve-note">{t('veNoStraps')}</p>}
              <ul className="ve-list">
                {project.straps.map((s) => (
                  <li key={s.id} className={strapSel?.id === s.id ? 'sel' : ''} onClick={() => setSel({ kind: 'strap', id: s.id })}>
                    <span className="ve-tc-small">{timecode(s.atMs)}</span> {s.lines.join(' · ').trim() || t('veStrapEmpty')}
                  </li>
                ))}
              </ul>
              {strapSel && (
                <div className="ve-form">
                  <input type="text" value={strapSel.lines[0] ?? ''} maxLength={200} placeholder={t('hubLine1')} disabled={!canEdit} onChange={(e) => patchStrap(strapSel.id, { lines: [e.target.value, strapSel.lines[1] ?? ''].filter((l, k) => k === 0 || l.trim()), template: strapSel.lines[1]?.trim() ? 'lower-third-2' : 'lower-third-1' }, `l1:${strapSel.id}`)} />
                  <input type="text" value={strapSel.lines[1] ?? ''} maxLength={200} placeholder={t('hubLine2')} disabled={!canEdit} onChange={(e) => patchStrap(strapSel.id, { lines: e.target.value.trim() ? [strapSel.lines[0] ?? '', e.target.value] : [strapSel.lines[0] ?? ''], template: e.target.value.trim() ? 'lower-third-2' : 'lower-third-1' }, `l2:${strapSel.id}`)} />
                  <div className="ve-field">
                    <label>{t('veStrapAt')}</label>
                    <input type="number" min={0} step={0.1} value={s1(strapSel.atMs)} disabled={!canEdit} onChange={(e) => patchStrap(strapSel.id, { atMs: Math.round(Number(e.target.value) * 1000) }, `at:${strapSel.id}`)} />
                    <span className="ve-unit">″</span>
                    <button className="btn small" type="button" disabled={!canEdit} onClick={() => patchStrap(strapSel.id, { atMs: Math.round(tRef.current) })} title={t('veStrapHere')}>
                      ⌖
                    </button>
                  </div>
                  <div className="ve-field">
                    <label>{t('veStrapDur')}</label>
                    <input type="number" min={0.5} step={0.5} value={s1(strapSel.durationMs)} disabled={!canEdit} onChange={(e) => patchStrap(strapSel.id, { durationMs: Math.round(Number(e.target.value) * 1000) }, `dur:${strapSel.id}`)} />
                    <span className="ve-unit">″</span>
                    <span className="ve-grow" />
                    <button className="btn small danger" type="button" disabled={!canEdit} onClick={() => removeStrap(strapSel.id)} title={t('delete')}>
                      ✖
                    </button>
                  </div>
                  <p className="ve-note ve-anim-head">✦ {t('veAnimation')}</p>
                  <AnimFields {...strapAnim(strapSel)} canEdit={canEdit} typewriter={false} onChange={(patch, key) => patchStrap(strapSel.id, patch, key ? `${key}:${strapSel.id}` : '')} />
                </div>
              )}
              <h4>
                ✦ {t('hubAi')}
                <button className="btn small" type="button" disabled={!!busy} onClick={suggest} title={t('hubAiHint')}>
                  {busy === 'ai' ? '…' : t('hubAiRun')}
                </button>
              </h4>
              {ai && (
                <div className="ve-ai">
                  {ai.people.length === 0 && ai.captions.length === 0 && <p className="ve-note">{t('hubAiNothing')}</p>}
                  {ai.people.map((p, i) => (
                    <div key={`p${i}`} className="ve-ai-row">
                      <span>
                        <b>{p.name}</b>
                        {p.role ? ` · ${p.role}` : ''}
                      </span>
                      <button type="button" className="btn small" disabled={!canEdit} onClick={() => addStrap([p.name, p.role].filter(Boolean))} title={t('hubAiAddAtNow')}>
                        ＋ {t('hubAiAdd')}
                      </button>
                    </div>
                  ))}
                  {ai.captions.map((c, i) => (
                    <div key={`c${i}`} className="ve-ai-row">
                      <span>{c}</span>
                      <button type="button" className="btn small" disabled={!canEdit} onClick={() => addStrap([c])} title={t('hubAiAddAtNow')}>
                        ＋ {t('hubAiAdd')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {sel.kind === 'track' && sel.track === 'vo' && (
            <section className="ve-panel">
              <h4>🎙 {t('veTrackVo')}</h4>
              <TrackStrip label="A1" track={project.mix.vo} canEdit={canEdit} onChange={(patch, key) => patchTrack('vo', patch, key)} />
              <ul className="ve-list">
                {voClips.map((c) => (
                  <li key={c.id} onClick={() => setSel({ kind: 'audio', id: c.id })}>
                    <span className="ve-tc-small">
                      {timecode(c.atMs)} – {timecode(audioEndMs(c))}
                    </span>{' '}
                    {audioInfo(c.source).title}
                  </li>
                ))}
              </ul>
              <h4>{t('veTakes')}</h4>
              <ul className="ve-list">
                {voiceovers.map((v) => (
                  <li key={v.id}>
                    <span>{v.kind === 'ai' ? `✦ ${v.voice ?? t('voAi')}` : `🎙 ${v.createdBy ?? t('voRecorded')}`}</span>
                    <span className="ve-grow" />
                    <button type="button" className="btn small" disabled={!canEdit} onClick={() => void addAudio({ kind: 'voiceover', id: v.id }, 'vo', v.durationMs)} title={t('veAddAtPlayhead')}>
                      ＋
                    </button>
                  </li>
                ))}
              </ul>
              <VoiceoverPanel storyId={edit.storyId} mediaAssetId={edit.mediaAssetId} text={edit.storyText} canEdit={canEdit} onChanged={() => void loadVo()} />
            </section>
          )}

          {sel.kind === 'track' && sel.track === 'music' && (
            <section className="ve-panel">
              <h4>♪ {t('veTrackMusic')}</h4>
              <TrackStrip label="A2" track={project.mix.music} canEdit={canEdit} onChange={(patch, key) => patchTrack('music', patch, key)} />
              <label className="ve-check-line">
                <input type="checkbox" checked={project.mix.music.duck} disabled={!canEdit} onChange={(e) => patchTrack('music', { duck: e.target.checked })} /> {t('veDuck')}
              </label>
              {project.mix.music.duck && (
                <div className="ve-field">
                  <label>{t('veDuckLevel')}</label>
                  <input type="range" min={0} max={1} step={0.05} value={project.mix.music.duckLevel} disabled={!canEdit} onChange={(e) => patchTrack('music', { duckLevel: Number(e.target.value) }, 'duck')} />
                  <span className="ve-unit">{Math.round(project.mix.music.duckLevel * 100)}%</span>
                </div>
              )}
              <ul className="ve-list">
                {musicClips.map((c) => (
                  <li key={c.id} onClick={() => setSel({ kind: 'audio', id: c.id })}>
                    <span className="ve-tc-small">
                      {timecode(c.atMs)} – {timecode(audioEndMs(c))}
                    </span>{' '}
                    {audioInfo(c.source).title}
                  </li>
                ))}
              </ul>
              <h4>{t('veBeds')}</h4>
              <ul className="ve-list">
                {music.map((m) => (
                  <li key={m.id}>
                    <span>♪ {m.title}</span>
                    <span className="ve-grow" />
                    <button type="button" className="btn small" disabled={!canEdit} onClick={() => void addAudio({ kind: 'music', id: m.id }, 'music', m.durationMs)} title={t('veAddAtPlayhead')}>
                      ＋
                    </button>
                  </li>
                ))}
              </ul>
              <label className="btn small">
                ⤒ {busy === 'music' ? '…' : t('veUploadMusic')}
                <input type="file" accept="audio/*" hidden disabled={!canEdit || !!busy} onChange={(e) => e.target.files?.[0] && uploadMusic(e.target.files[0])} />
              </label>
              <h4>
                ▽ {t('veDips')}
                <button className="btn small" type="button" disabled={!canEdit || !hasMusic} onClick={() => addDip()} title={hasMusic ? t('veDipAddHint') : t('veDipNeedsMusic')}>
                  ＋ {t('veDipAdd')}
                </button>
              </h4>
              {project.dips.length === 0 && <p className="ve-note">{t('veNoDips')}</p>}
              <ul className="ve-list">
                {project.dips.map((d) => (
                  <li key={d.id} onClick={() => setSel({ kind: 'dip', id: d.id })}>
                    <span className="ve-tc-small">
                      {timecode(d.fromMs)} – {timecode(d.toMs)}
                    </span>{' '}
                    ▽ {Math.round(d.level * 100)}%
                  </li>
                ))}
              </ul>
              <p className="ve-note">{t('veMusicNote')}</p>
            </section>
          )}

          {sel.kind === 'track' && sel.track === 'sound' && (
            <section className="ve-panel">
              <h4>🔊 {t('veTrackSound')}</h4>
              <TrackStrip label="A3" track={project.mix.sound} canEdit={canEdit} onChange={(patch, key) => patchTrack('sound', patch, key)} />
              {soundClips.length === 0 && <p className="ve-note">{t('veSoundNoClips')}</p>}
              <ul className="ve-list">
                {soundClips.map((c) => (
                  <li key={c.id} onClick={() => setSel({ kind: 'audio', id: c.id })}>
                    <span className="ve-tc-small">
                      {timecode(c.atMs)} – {timecode(audioEndMs(c))}
                    </span>{' '}
                    🔊 {audioInfo(c.source).title}
                  </li>
                ))}
              </ul>
              <p className="ve-note">{t('veSoundTrackHint')}</p>
            </section>
          )}

          {sel.kind === 'audio' && selectedAudio && (
            <section className="ve-panel">
              <h4>
                {TRACK[selectedAudio.track].icon} {audioInfo(selectedAudio.source).title}
                <button className="btn small" type="button" onClick={() => setSel({ kind: 'track', track: selectedAudio.track })}>
                  {TRACK[selectedAudio.track].code}
                </button>
              </h4>
              <div className="ve-field">
                <label>{t('veStrapAt')}</label>
                <input type="number" min={0} step={0.1} value={s1(selectedAudio.atMs)} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { atMs: Math.round(Number(e.target.value) * 1000) }, `at:${selectedAudio.id}`)} />
                <span className="ve-unit">″</span>
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => patchAudio(selectedAudio.id, { atMs: Math.round(tRef.current) })} title={t('veStrapHere')}>
                  ⌖
                </button>
              </div>
              <div className="ve-field">
                <label>{t('veAudioIn')}</label>
                <input type="number" min={0} step={0.1} value={s1(selectedAudio.inMs)} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { inMs: Math.round(Number(e.target.value) * 1000) }, `in:${selectedAudio.id}`)} />
                <span className="ve-unit">″</span>
                <label>{t('veAudioOut')}</label>
                <input type="number" min={0} step={0.1} value={s1(selectedAudio.outMs)} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { outMs: Math.round(Number(e.target.value) * 1000) }, `out:${selectedAudio.id}`)} />
                <span className="ve-unit">″</span>
              </div>
              <div className="ve-field">
                <label>{t('veLength')}</label>
                <span className="ve-tc-small">{timecode(audioLengthMs(selectedAudio))}</span>
              </div>
              <div className="ve-field">
                <label>{t('veLevel')}</label>
                <input type="range" min={-30} max={6} step={0.5} value={Math.max(-30, toDb(selectedAudio.gain))} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { gain: Number(e.target.value) <= -30 ? 0 : fromDb(Number(e.target.value)) }, `gain:${selectedAudio.id}`)} />
                <span className="ve-unit">{dbLabel(selectedAudio.gain)}</span>
              </div>
              <div className="ve-field">
                <label>{t('veFadeIn')}</label>
                <input type="number" min={0} max={30} step={0.1} value={s1(selectedAudio.fadeInMs)} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { fadeInMs: Math.round(Number(e.target.value) * 1000) }, `fi:${selectedAudio.id}`)} />
                <span className="ve-unit">″</span>
                <label>{t('veFadeOut')}</label>
                <input type="number" min={0} max={30} step={0.1} value={s1(selectedAudio.fadeOutMs)} disabled={!canEdit} onChange={(e) => patchAudio(selectedAudio.id, { fadeOutMs: Math.round(Number(e.target.value) * 1000) }, `fo:${selectedAudio.id}`)} />
                <span className="ve-unit">″</span>
              </div>
              <div className="ve-field">
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => normalise(selectedAudio)} title={t('veNormaliseHint')}>
                  ⇱ {t('veNormalise')}
                </button>
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => cut(false)} title={t('veCutHere')}>
                  ✂ {t('veCutHere')}
                </button>
                {selectedAudio.link && (
                  <button className="btn small" type="button" disabled={!canEdit} onClick={() => toggleSoundApart({ kind: 'audio', id: selectedAudio.id })} title={t('veSoundRelinkHint')}>
                    🔗 {t('veSoundRelink')}
                  </button>
                )}
                <span className="ve-grow" />
                <button className="btn small danger" type="button" disabled={!canEdit} onClick={() => removeAudio(selectedAudio.id)} title={t('delete')}>
                  🗑 {t('veRemovePiece')}
                </button>
              </div>
              <p className="ve-note">{selectedAudio.track === 'sound' ? t('veSoundClipHint') : selectedAudio.track === 'vo' ? t('veVoClipHint') : t('veMusicClipHint')}</p>
            </section>
          )}

          {sel.kind === 'dip' && dipSel && (
            <section className="ve-panel">
              <h4>
                ▽ {t('veDip')}
                <button className="btn small" type="button" onClick={() => setSel({ kind: 'track', track: 'music' })}>
                  ♪ A2
                </button>
              </h4>
              <div className="ve-field">
                <label>{t('veDipLevel')}</label>
                <input type="range" min={0} max={1} step={0.05} value={dipSel.level} disabled={!canEdit} onChange={(e) => patchDip(dipSel.id, { level: Number(e.target.value) }, `lv:${dipSel.id}`)} />
                <span className="ve-unit">{Math.round(dipSel.level * 100)}%</span>
              </div>
              <div className="ve-field">
                <label>{t('veDipFrom')}</label>
                <input type="number" min={0} step={0.1} value={s1(dipSel.fromMs)} disabled={!canEdit} onChange={(e) => patchDip(dipSel.id, { fromMs: Math.round(Number(e.target.value) * 1000) }, `from:${dipSel.id}`)} />
                <span className="ve-unit">″</span>
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => patchDip(dipSel.id, { fromMs: Math.round(tRef.current) })} title={t('veStrapHere')}>
                  ⌖
                </button>
              </div>
              <div className="ve-field">
                <label>{t('veDipTo')}</label>
                <input type="number" min={0} step={0.1} value={s1(dipSel.toMs)} disabled={!canEdit} onChange={(e) => patchDip(dipSel.id, { toMs: Math.round(Number(e.target.value) * 1000) }, `to:${dipSel.id}`)} />
                <span className="ve-unit">″</span>
                <button className="btn small" type="button" disabled={!canEdit} onClick={() => patchDip(dipSel.id, { toMs: Math.round(tRef.current) })} title={t('veStrapHere')}>
                  ⌖
                </button>
                <span className="ve-grow" />
                <button className="btn small danger" type="button" disabled={!canEdit} onClick={() => removeDip(dipSel.id)} title={t('delete')}>
                  ✖
                </button>
              </div>
              <p className="ve-note">{t('veDipHint')}</p>
            </section>
          )}

          {sel.kind === 'mix' && (
            <section className="ve-panel">
              <h4>🎚 {t('veMixer')}</h4>
              <TrackStrip label={`V1 · ${t('veTrackVideo')}`} track={project.mix.video} canEdit={canEdit} onChange={(patch, key) => patchTrack('video', patch, key)} />
              <TrackStrip label={`A1 · ${t('veTrackVo')}`} track={project.mix.vo} canEdit={canEdit} onChange={(patch, key) => patchTrack('vo', patch, key)} />
              <TrackStrip label={`A2 · ${t('veTrackMusic')}`} track={project.mix.music} canEdit={canEdit} onChange={(patch, key) => patchTrack('music', patch, key)} />
              <TrackStrip label={`A3 · ${t('veTrackSound')}`} track={project.mix.sound} canEdit={canEdit} onChange={(patch, key) => patchTrack('sound', patch, key)} />
              <h4>{t('veEndFade')}</h4>
              <div className="ve-field">
                <select
                  value={project.endFade ? `${project.endFade.color}:${project.endFade.durationMs}` : ''}
                  disabled={!canEdit}
                  onChange={(e) => {
                    const [color, ms] = e.target.value.split(':');
                    commit((p) => ({ ...p, endFade: e.target.value ? { color: color as 'black' | 'white', durationMs: Number(ms) } : null }));
                  }}
                >
                  <option value="">{t('veEndFadeNone')}</option>
                  {[500, 1000, 2000].map((ms) => (
                    <option key={`b${ms}`} value={`black:${ms}`}>
                      {t('veEndFadeBlack')} · {ms / 1000}″
                    </option>
                  ))}
                  {[1000].map((ms) => (
                    <option key={`w${ms}`} value={`white:${ms}`}>
                      {t('veEndFadeWhite')} · {ms / 1000}″
                    </option>
                  ))}
                </select>
              </div>
              <p className="ve-note">{t('veSoundHint')}</p>
            </section>
          )}

          </div>
        </aside>
        <nav className="ve-rrail" aria-label={t('veInspector')}>
          {railItems.map((it) => (
            <button key={it.id} type="button" className={`ve-rail-item${it.on ? ' sel' : ''}`} onClick={it.go} title={it.label}>
              <span className="ve-rail-icon">{it.icon}</span>
              <span className="ve-rail-label">{it.label}</span>
            </button>
          ))}
          <span className="ve-grow" />
          <button type="button" className={`ve-rail-item${sel.kind === 'mix' ? ' sel' : ''}`} onClick={() => setSel({ kind: 'mix' })} title={t('veMixer')}>
            <span className="ve-rail-icon">🎚</span>
            <span className="ve-rail-label">{t('veMixer')}</span>
          </button>
          <button type="button" className={`ve-rail-item${sel.kind === 'format' ? ' sel' : ''}`} onClick={() => setSel({ kind: 'format' })} title={t('veBinFormat')}>
            <span className="ve-rail-icon">▭</span>
            <span className="ve-rail-label">{project.format.aspect}</span>
          </button>
        </nav>
      </div>

      <div className="ve-splitter" role="separator" aria-orientation="horizontal" title={t('veSplitterHint')} onPointerDown={onSplitDown} />

      <section className="ve-tl" style={{ height: tlH }}>
        <div className="ve-tl-bar">
          <button className="btn icon" type="button" disabled={!hist.past} onClick={undo} title={t('veUndo')}>
            ↶
          </button>
          <button className="btn icon" type="button" disabled={!hist.future} onClick={redo} title={t('veRedo')}>
            ↷
          </button>
          <span className="ve-sep" />
          <button className="btn icon" type="button" disabled={!canEdit} onClick={() => cut(false)} title={t('veSplitHint')}>
            ✂
          </button>
          <button className="btn icon" type="button" disabled={!canEdit} onClick={() => cut(true)} title={t('veSplitAllHint')}>
            ✂✂
          </button>
          <button className="btn icon" type="button" disabled={!canEdit || (!(selectedSegment && project.segments.length > 1) && !selectedAudio && sel.kind !== 'strap' && sel.kind !== 'dip' && !textSel && !overlaySel)} onClick={() => removeSel(sel)} title={t('veRemovePieceHint')}>
            🗑
          </button>
          <button className="btn icon" type="button" disabled={!canEdit || !(selectedSegment || selectedAudio || textSel || overlaySel || sel.kind === 'strap')} onClick={() => duplicate(sel)} title={`${t('veDuplicate')} (Ctrl+D)`}>
            ⧉
          </button>
          <button className="btn icon" type="button" disabled={!canEdit} onClick={freeze} title={t('veFreezeHint')}>
            ❄
          </button>
          <button className={`btn icon${soundLinkSel ? ' on' : ''}`} type="button" disabled={!canEdit || !(soundLinkSel || (selectedSegment && pieceSounds(selectedSegment)))} onClick={() => toggleSoundApart()} title={`${soundLinkSel ? t('veSoundRelinkHint') : t('veSoundDetachHint')} (L)`}>
            {soundLinkSel ? '🔗' : '✂♪'}
          </button>
          <button className="btn icon" type="button" disabled={!canEdit || !selRef} onClick={() => selRef && toggleAllKeys(selRef)} title={t('veKeyAllHint')}>
            ◆
          </button>
          <span className="ve-sep" />
          <button className="btn icon" type="button" disabled={!canEdit} onClick={() => addText('subtitle')} title={t('veTextAddHint')}>
            Aa
          </button>
          <button className="btn icon" type="button" disabled={!canEdit} onClick={() => addStrap([''], tRef.current, 'lower-third-2')} title={t('veAddStrapHint')}>
            T
          </button>
          <button className="btn icon" type="button" disabled={!canEdit || project.segments.length < 2} onClick={() => putTransition('dissolve')} title={t('veTrAddHint')}>
            ⧓
          </button>
          <button className="btn icon" type="button" disabled={!canEdit || !hasMusic} onClick={() => addDip()} title={hasMusic ? t('veDipAddHint') : t('veDipNeedsMusic')}>
            ▽
          </button>
          <span className="ve-grow" />
          <span className="ve-tl-chip" title={t('veMainSequence')}>
            ▦ {t('veMainSequence')} · {project.segments.length} {t('vePieces')}
          </span>
          <span className="ve-grow" />
          {analysing && <span className="ve-analysing">{t('veAnalysing')}</span>}
          <button className={`btn icon${snap ? ' on' : ''}`} type="button" onClick={() => setSnap((x) => !x)} title={t('veSnapHint')}>
            🧲
          </button>
          <button className="btn icon" type="button" onClick={() => setZoom(null)} title={t('veFitHint')}>
            ⤢
          </button>
          <button className="btn icon" type="button" onClick={() => setZoom(Math.max(0, zoomIdx - 1))} title={t('veZoomOut')}>
            −
          </button>
          <input className="ve-zoom" type="range" min={0} max={SCALES.length - 1} step={1} value={zoomIdx} onChange={(e) => setZoom(Number(e.target.value))} />
          <button className="btn icon" type="button" onClick={() => setZoom(Math.min(SCALES.length - 1, zoomIdx + 1))} title={t('veZoomIn')}>
            +
          </button>
        </div>
        <div className={`ve-tl-body${dropOver ? ' drop' : ''}`} onDragOver={onDragOver} onDragLeave={() => setDropOver(false)} onDrop={onDrop}>
          <div className="ve-heads" ref={headsRef}>
            <div className="ve-head-spacer" />
            {ROW_KEYS.map((k) => {
              const lane = k as Lane;
              const hiddenL = project.lanes.hidden.includes(lane);
              const lockedL = project.lanes.locked.includes(lane);
              const ms = muteSolo(k);
              const tr = ms ? project.mix[ms] : null;
              return (
                <div key={k} role="button" tabIndex={-1} className={`ve-head${selRow === k ? ' sel' : ''}${hiddenL ? ' off' : ''}`} style={{ height: ROW_HEIGHT[k] }} onClick={() => setSel(selectionOfRow(ROW[k], firstId))}>
                  <b>{TRACK[k].code}</b>
                  <span className="ve-head-name">
                    {TRACK[k].icon} {TRACK[k].name()}
                  </span>
                  <span className="ve-head-tools">
                    {(k === 'overlay' || k === 'video' || k === 'straps' || k === 'text') && (
                      <button
                        type="button"
                        className={hiddenL ? 'on' : ''}
                        disabled={!canEdit}
                        title={hiddenL ? t('veShowTrack') : t('veHideTrack')}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLane('hidden', lane);
                        }}
                      >
                        {hiddenL ? '⊘' : '👁'}
                      </button>
                    )}
                    {ms && tr && (
                      <>
                        <button
                          type="button"
                          className={tr.mute ? 'on mute' : ''}
                          disabled={!canEdit}
                          title={t('veMute')}
                          onClick={(e) => {
                            e.stopPropagation();
                            patchTrack(ms, { mute: !tr.mute });
                          }}
                        >
                          M
                        </button>
                        <button
                          type="button"
                          className={tr.solo ? 'on solo' : ''}
                          disabled={!canEdit}
                          title={t('veSolo')}
                          onClick={(e) => {
                            e.stopPropagation();
                            patchTrack(ms, { solo: !tr.solo });
                          }}
                        >
                          S
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className={lockedL ? 'on lock' : ''}
                      disabled={!canEdit}
                      title={lockedL ? t('veUnlockTrack') : t('veLockTrack')}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleLane('locked', lane);
                      }}
                    >
                      {lockedL ? '🔒' : '🔓'}
                    </button>
                    {k === 'video' && (
                      <span className="ve-magnet" title={t('veMagnetHint')}>
                        🧲
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div
            className="ve-tl-wrap"
            ref={wrapRef}
            onPointerDownCapture={(e) => {
              tlDownRef.current = { x: e.clientX, y: e.clientY };
            }}
          >
            <Timeline
              ref={tlRef}
              editorData={rows}
              effects={EFFECTS}
              scale={scale}
              scaleWidth={SCALE_WIDTH}
              scaleSplitCount={5}
              startLeft={START_LEFT}
              minScaleCount={scaleCount}
              maxScaleCount={scaleCount}
              rowHeight={40}
              gridSnap={false}
              dragLine={snap}
              autoScroll
              disableDrag={!canEdit}
              style={{ width: '100%', height: Math.max(80, tlH - TL_BAR) }}
              getScaleRender={(s) => <span className="ve-scale">{scaleLabel(s, scale)}</span>}
              getActionRender={(action) => renderAction(action)}
              onChange={onTimelineChange}
              onActionResizing={({ action, start, end }) => (segmentIdOf(action.id) ? end - start >= 0.04 : audioIdOf(action.id) ? end - start >= 0.1 : textIdOf(action.id) || overlayIdOf(action.id) ? end - start >= 0.2 : end - start >= 0.5)}
              onClickAction={(_e, { action }) => {
                const s = selectionOf(action.id);
                if (s) setSel(s);
              }}
              // a click on an item also puts the playhead where it landed; the click that ends a drag or a trim does not
              onClickActionOnly={(e, { time }) => {
                if (!pressMoved(e)) seek(time * 1000);
              }}
              onClickRow={(e, { row, time }) => {
                // a click on the empty track seeks and selects the track; the click that ends a drag on an item does not
                if (pressMoved(e) || (e.target as HTMLElement).closest('.timeline-editor-action')) return;
                seek(time * 1000);
                setSel(selectionOfRow(row.id, firstId));
              }}
              onDoubleClickRow={(_e, { row, time }) => {
                if (!canEdit) return;
                if (row.id === ROW.straps) addStrap([''], time * 1000);
                else if (row.id === ROW.text) addText('subtitle', time * 1000);
              }}
              onClickTimeArea={(time) => {
                seek(time * 1000);
                return true;
              }}
              onCursorDrag={(time) => seek(time * 1000)}
              onContextMenuAction={(e, { action }) => {
                e.preventDefault();
                const x = selectionOf(action.id);
                if (!x) return;
                setSel(x);
                setMenu({ x: Math.min(e.clientX, window.innerWidth - 230), y: Math.min(e.clientY, window.innerHeight - 330), sel: x });
              }}
              onScroll={({ scrollLeft, scrollTop }) => {
                viewRef.current.scrollLeft = scrollLeft;
                if (headsRef.current) headsRef.current.scrollTop = scrollTop;
              }}
            />
          </div>
        </div>
      </section>

      {/* the menu's actions read refs only when clicked */}
      {/* eslint-disable react-hooks/refs */}
      {menu && (
        <div className="ve-menu" role="menu" style={{ left: menu.x, top: menu.y }}>
          {menuItems(menu.sel).map((it, i) =>
            it === '-' ? (
              <hr key={`sep${i}`} />
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                disabled={!canEdit || it.off}
                onClick={() => {
                  setMenu(null);
                  it.go();
                }}
              >
                <span className="ve-menu-icon">{it.icon}</span>
                <span className="ve-menu-label">{it.label}</span>
                {it.kbd && <kbd>{it.kbd}</kbd>}
              </button>
            ),
          )}
        </div>
      )}
      {/* eslint-enable react-hooks/refs */}

      {airOpen && <AirDialog editId={edit.id} view={air.view} refresh={air.refresh} canEdit={canEdit} prepare={prepareAir} onClose={() => setAirOpen(false)} />}
      {aiOpen && (
        <AiEditDialog
          editId={edit.id}
          onClose={() => setAiOpen(false)}
          onApply={applyAiEdit}
          candidates={[
            ...sources.map((s) => ({ key: s.key, source: s.source, title: sourceInfo(s.source).title, durationMs: sourceInfo(s.source).durationMs ?? 0, inEdit: true })),
            ...videoUploads.filter((u) => !sources.some((s) => s.key === `upload:${u.id}`)).map((u) => ({ key: `upload:${u.id}`, source: { kind: 'upload' as const, id: u.id }, title: u.title, durationMs: u.durationMs ?? 0, inEdit: false })),
            ...library.filter((c) => !sources.some((s) => s.key === `clip:${c.id}`)).map((c) => ({ key: `clip:${c.id}`, source: { kind: 'clip' as const, id: c.id }, title: c.title, durationMs: c.durationMs, inEdit: false })),
          ]}
          hasTakes={voiceovers.length > 0}
          hasBeds={music.length > 0}
        />
      )}
    </div>
    </StrapDesignContext.Provider>
  );
}

/**
 * A piece on V1: its source's filmstrip at the ruler's scale (the piece's own range, at its speed), its name, its
 * waveform, its transition in; a photo shows itself along the piece, a frozen frame its frame; badges for its speed,
 * look and effects.
 */
function PieceChip({ action, segment, analysis, pxPerS, title, selected }: { action: TlAction; segment: EditSegment; analysis: SourceAnalysis | null; pxPerS: number; title: string; selected: boolean }) {
  const w = (action.end - action.start) * pxPerS;
  const inS = segment.inMs / 1000;
  const speed = isStill(segment) ? 1 : segment.speed;
  const shown: Array<{ tS: number; url: string; x: number }> = [];
  let stillUrl: string | null = segment.source.kind === 'image' ? `/api/uploads/${encodeURIComponent(segment.source.id)}/file` : null;
  if (segment.freeze) {
    // the thumbnail nearest the frozen frame, all along the piece
    let best: { tS: number; url: string } | null = null;
    for (const th of analysis?.thumbs ?? []) if (!best || Math.abs(th.tS - inS) < Math.abs(best.tS - inS)) best = th;
    stillUrl = best?.url ?? null;
  } else if (!stillUrl) {
    let nextX = -Infinity;
    for (const th of analysis?.thumbs ?? []) {
      const x = ((th.tS - inS) / speed) * pxPerS - THUMB_W / 2;
      if (x + THUMB_W <= 0 || x >= w) continue;
      if (x >= nextX) {
        shown.push({ ...th, x });
        nextX = x + THUMB_W;
      }
    }
  }
  const srcS = analysis?.durationS ?? 0;
  const tr = segment.transitionIn;
  const badges = [segment.audioLink ? '♪A3' : '', segment.freeze ? '❄' : '', !isStill(segment) && segment.speed !== 1 ? `${segment.speed}×` : '', segment.look.filter !== 'none' ? '◐' : '', segment.motion.type !== 'none' ? '⊕' : '', segment.effects.length ? '✨' : ''].filter(Boolean);
  return (
    <div className={`ve-act clip ${segment.source.kind}${selected ? ' sel' : ''}${segment.gain === 0 || !pieceSounds(segment) ? ' silent' : ''}${segment.freeze ? ' frozen' : ''}`}>
      <div className="ve-film" style={{ height: THUMB_H, ...(stillUrl ? { backgroundImage: `url(${stillUrl})`, backgroundSize: `${THUMB_W}px ${THUMB_H}px`, backgroundRepeat: 'repeat-x' } : {}) }}>
        {shown.map((s) => (
          <img key={s.tS} src={s.url} alt="" draggable={false} style={{ left: s.x, width: THUMB_W, height: THUMB_H }} />
        ))}
        <span className="ve-act-title">{title}</span>
        <span className="ve-act-sub">{segment.source.kind === 'image' ? `${(segmentLengthMs(segment) / 1000).toFixed(1)}″` : segment.freeze ? timecode(segment.inMs) : `${timecode(segment.inMs)} – ${timecode(segment.outMs)}`}</span>
        {badges.length > 0 && <span className="ve-act-badges">{badges.join(' ')}</span>}
      </div>
      <div className="ve-clip-wave" style={pieceSounds(segment) && analysis?.wave && srcS > 0 ? { backgroundImage: `url(${analysis.wave})`, backgroundSize: `${Math.max(1, (srcS / speed) * pxPerS)}px 100%`, backgroundPosition: `${(-inS / speed) * pxPerS}px 0` } : undefined} />
      <KeyMarks keys={segment.keys} pxPerS={pxPerS} />
      {tr && <span className={`ve-tr-mark ${tr.type}`} style={{ width: Math.max(10, (tr.durationMs / 2000) * pxPerS) }} title={`${TRANSITION_WORDS[tr.type]()} · ${s1(tr.durationMs)}″`} />}
    </div>
  );
}

/** An item's keyframes as small diamonds along its chip on the timeline. */
function KeyMarks({ keys, pxPerS }: { keys: EditKeys; pxPerS: number }) {
  const times = keyTimes(keys);
  if (!times.length) return null;
  return (
    <span className="ve-kmarks" aria-hidden="true">
      {times.map((k) => (
        <i key={k} style={{ left: (k / 1000) * pxPerS }}>
          ◆
        </i>
      ))}
    </span>
  );
}

/** An audio clip on A1 or A2: the part of its file's waveform it plays, its fades as ramps, its name and level. */
function AudioChip({ clip, wave, pxPerS, title, selected }: { clip: EditAudioClip; wave: Wave | null; pxPerS: number; title: string; selected: boolean }) {
  const fileS = wave ? wave.durationMs / 1000 : 0;
  const style: React.CSSProperties = wave && fileS > 0 ? { backgroundImage: `url(${wave.wave})`, backgroundSize: `${Math.max(1, fileS * pxPerS)}px 100%`, backgroundPosition: `${-(clip.inMs / 1000) * pxPerS}px 0` } : {};
  return (
    <div className={`ve-act ${clip.track}${selected ? ' sel' : ''}`} style={style}>
      {clip.fadeInMs > 0 && <span className="ve-fade in" style={{ width: (clip.fadeInMs / 1000) * pxPerS }} />}
      {clip.fadeOutMs > 0 && <span className="ve-fade out" style={{ width: (clip.fadeOutMs / 1000) * pxPerS }} />}
      <span className="ve-act-text">
        {title}
        {Math.abs(toDb(clip.gain)) >= 0.5 ? ` · ${dbLabel(clip.gain)}` : ''}
      </span>
    </div>
  );
}

/** A track's level, mute and solo, as one mixer strip. */
function TrackStrip({ label, track, canEdit, onChange }: { label: string; track: { gain: number; mute: boolean; solo: boolean }; canEdit: boolean; onChange: (patch: Partial<{ gain: number; mute: boolean; solo: boolean }>, key?: string) => void }) {
  return (
    <div className="ve-strip">
      <span className="ve-strip-name">{label}</span>
      <input type="range" min={-30} max={6} step={0.5} value={Math.max(-30, toDb(track.gain))} disabled={!canEdit} onChange={(e) => onChange({ gain: Number(e.target.value) <= -30 ? 0 : fromDb(Number(e.target.value)) }, `track:${label}`)} />
      <span className="ve-unit">{dbLabel(track.gain)}</span>
      <span className="ve-ms">
        <button type="button" className={track.mute ? 'on mute' : ''} disabled={!canEdit} onClick={() => onChange({ mute: !track.mute })} title={t('veMute')}>
          M
        </button>
        <button type="button" className={track.solo ? 'on solo' : ''} disabled={!canEdit} onClick={() => onChange({ solo: !track.solo })} title={t('veSolo')}>
          S
        </button>
      </span>
    </div>
  );
}

interface AiCandidate {
  key: string;
  source: EditSource;
  title: string;
  durationMs: number;
  inEdit: boolean;
}

/**
 * The AI editor's dialog: which clips it may use (those of the edit and the imports ticked, the library's on a
 * search), an instruction, a length; it captures a few frames of each clip in the browser, sends them with the story
 * and gets back an edit, which the page applies as one change (Ctrl+Z takes it back).
 */
function AiEditDialog({ editId, candidates, hasTakes, hasBeds, onClose, onApply }: { editId: string; candidates: AiCandidate[]; hasTakes: boolean; hasBeds: boolean; onClose: () => void; onApply: (p: EditProject, notes: string) => void }) {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(candidates.filter((c) => c.inEdit || c.source.kind === 'upload').map((c) => c.key)));
  const [q, setQ] = useState('');
  const [instruction, setInstruction] = useState('');
  const [target, setTarget] = useState('');
  const [useVoiceover, setUseVoiceover] = useState(hasTakes);
  const [useMusic, setUseMusic] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shown = candidates.filter((c) => c.inEdit || c.source.kind === 'upload' || picked.has(c.key) || (q.trim() && c.title.toLowerCase().includes(q.trim().toLowerCase()))).slice(0, 40);
  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < 12) next.add(key);
      return next;
    });
  const go = async () => {
    setError(null);
    try {
      const chosen = candidates.filter((c) => picked.has(c.key) && c.durationMs > 0);
      if (!chosen.length) throw new Error(t('veAiPickOne'));
      const clips = [];
      for (const [i, c] of chosen.entries()) {
        setStep(`${t('veAiFrames')} ${i + 1}/${chosen.length}`);
        const n = clamp(Math.round(c.durationMs / 3000), 2, 6);
        const times = Array.from({ length: n }, (_, k) => ((k + 0.5) * c.durationMs) / n / 1000);
        const frames = await captureFrames(sourceUrlOf(c.source), times);
        clips.push({ kind: c.source.kind, id: c.source.id, frames });
      }
      setStep(t('veAiThinking'));
      const secs = Number(target);
      const r = await post<{ project: EditProject; notes: string }>(`/api/edits/${editId}/ai`, { instruction, targetSeconds: Number.isFinite(secs) && secs >= 5 ? Math.round(secs) : null, useVoiceover, useMusic, clips });
      onApply(r.project, r.notes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setStep(null);
    }
  };
  return (
    <div className="ve-modal" role="dialog" aria-modal="true" onKeyDown={(e) => e.stopPropagation()}>
      <div className="ve-modal-box">
        <h3>✦ {t('veAiEdit')}</h3>
        <p className="ve-note">{t('veAiIntro')}</p>
        <label className="ve-modal-label">{t('veAiInstruction')}</label>
        <textarea rows={3} value={instruction} maxLength={600} placeholder={t('veAiInstructionPh')} onChange={(e) => setInstruction(e.target.value)} />
        <div className="ve-field">
          <label>{t('veAiTarget')}</label>
          <input type="number" min={5} max={600} step={5} value={target} placeholder={t('veAiTargetAuto')} onChange={(e) => setTarget(e.target.value)} />
          <span className="ve-unit">″</span>
          <label className="ve-check-line">
            <input type="checkbox" checked={useVoiceover} disabled={!hasTakes} onChange={(e) => setUseVoiceover(e.target.checked)} /> {t('veAiUseVo')}
          </label>
          <label className="ve-check-line">
            <input type="checkbox" checked={useMusic} disabled={!hasBeds} onChange={(e) => setUseMusic(e.target.checked)} /> {t('veAiUseMusic')}
          </label>
        </div>
        <label className="ve-modal-label">
          {t('veAiClips')} ({picked.size}/12)
        </label>
        <input type="search" value={q} placeholder={t('veAiSearch')} onChange={(e) => setQ(e.target.value)} />
        <div className="ve-modal-list">
          {shown.map((c) => (
            <label key={c.key} className={`ve-modal-clip${picked.has(c.key) ? ' on' : ''}`}>
              <input type="checkbox" checked={picked.has(c.key)} onChange={() => toggle(c.key)} />
              {c.source.kind === 'clip' ? <img alt="" src={`/api/media/${encodeURIComponent(c.source.id)}/thumb`} onError={(e) => (e.currentTarget.style.visibility = 'hidden')} /> : <span className="ve-bin-thumb blank">⤒</span>}
              <span className="ve-modal-clip-title">{c.title}</span>
              <span className="ve-tc-small">{mmss(Math.round(c.durationMs / 1000))}</span>
              {c.inEdit && <span className="ve-badge">{t('veAiInEdit')}</span>}
            </label>
          ))}
        </div>
        {error && <div className="banner err">{error}</div>}
        <div className="ve-modal-actions">
          <span className="ve-note">{step ?? t('veAiUndoNote')}</span>
          <span className="ve-grow" />
          <button type="button" className="btn" onClick={onClose} disabled={!!step}>
            {t('cancel')}
          </button>
          <button type="button" className="btn primary" onClick={() => void go()} disabled={!!step || picked.size === 0}>
            {step ? '…' : `✦ ${t('veAiGo')}`}
          </button>
        </div>
      </div>
    </div>
  );
}
